import { useState, useMemo, useEffect } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import {
  Workflow,
  Play,
  Pause,
  RotateCcw,
  CheckCircle2,
  XCircle,
  Clock,
  Loader2,
  Sparkles,
  ArrowRight,
  ArrowLeft,
  ChevronDown,
  ChevronUp,
  FileText,
  DollarSign,
  Calendar,
  Send,
  Eye,
  Wand2,
  Target,
  Star,
  Zap,
  ListChecks,
  Copy,
  Image,
  Users,
  Flame,
  Settings2,
  Pencil,
  X,
  Check,
  AlertCircle,
  Search,
  TrendingUp,
  RefreshCw,
  MessageSquare,
  Lightbulb,
  Globe,
  Megaphone,
} from "lucide-react";
import { orchestratorAPI, socialAPI, mediaAPI, postsAPI, getErrorMessage } from "../../services/api";
import AgentPipelineGraph from "./AgentPipelineGraph";
import ContentStrategistFlow from "./ContentStrategistFlow";

/* eslint-disable jsx-a11y/label-has-associated-control */

// ── Flow Definitions ────────────────────────────────────────

// Flows will use dynamic labels from translations
// Keeping id, icon, color, inputType, defaultPublishMode as before
const FLOWS_BASE: Array<{ id: string; icon: unknown; color: string; inputType: "topic" | "text"; defaultPublishMode: "draft" | "schedule" | "publish_now"; pauseBetweenSteps: boolean }> = [
  {
    id: "quick_post",
    icon: Zap,
    color: "from-amber-500 to-orange-500",
    inputType: "topic",
    defaultPublishMode: "schedule",
    pauseBetweenSteps: false,
  },
  {
    id: "full_control",
    icon: Settings2,
    color: "from-violet-500 to-purple-600",
    inputType: "topic",
    defaultPublishMode: "draft",
    pauseBetweenSteps: true,
  },
  {
    id: "repurpose",
    icon: Wand2,
    color: "from-teal-500 to-emerald-500",
    inputType: "text",
    defaultPublishMode: "draft",
    pauseBetweenSteps: false,
  },
  {
    id: "calendar",
    icon: Calendar,
    color: "from-blue-500 to-indigo-500",
    inputType: "topic",
    defaultPublishMode: "schedule",
    pauseBetweenSteps: false,
  },
  {
    id: "viral_rewrite",
    icon: Flame,
    color: "from-red-500 to-pink-500",
    inputType: "topic",
    defaultPublishMode: "schedule",
    pauseBetweenSteps: false,
  },
  {
    id: "strategist",
    icon: TrendingUp,
    color: "from-emerald-500 to-cyan-500",
    inputType: "topic",
    defaultPublishMode: "schedule",
    pauseBetweenSteps: false,
  },
];

const getFlowLabel = (t: ReturnType<typeof useTranslation>['t'], flowId: string): string => {
  const labels: Record<string, string> = {
    quick_post: t('orch_flow_quick_post_label'),
    full_control: t('orch_flow_full_control_label'),
    repurpose: t('orch_flow_repurpose_label'),
    calendar: t('orch_flow_calendar_label'),
    viral_rewrite: t('orch_flow_viral_label'),
    strategist: t('orch_flow_strategist_label', 'Content Strategist'),
  };
  return labels[flowId] || '';
};

const getFlowDesc = (t: ReturnType<typeof useTranslation>['t'], flowId: string): string => {
  const descs: Record<string, string> = {
    quick_post: t('orch_flow_quick_post_desc'),
    full_control: t('orch_flow_full_control_desc'),
    repurpose: t('orch_flow_repurpose_desc'),
    calendar: t('orch_flow_calendar_desc'),
    viral_rewrite: t('orch_flow_viral_desc'),
    strategist: t('orch_flow_strategist_desc', 'AI plans your content calendar then generates, reviews & schedules each post with feedback loops'),
  };
  return descs[flowId] || '';
};

const getFlowStepsLabel = (t: ReturnType<typeof useTranslation>['t'], flowId: string): string => {
  const steps: Record<string, string> = {
    quick_post: t('orch_flow_quick_post_steps'),
    full_control: t('orch_flow_quick_post_steps'),
    repurpose: t('orch_flow_repurpose_steps'),
    calendar: t('orch_flow_calendar_steps'),
    viral_rewrite: t('orch_flow_viral_steps'),
    strategist: t('orch_flow_strategist_steps', 'Plan → Generate × N → Review loops → Schedule'),
  };
  return steps[flowId] || '';
};

// Dynamic FLOWS construction in main component

const PLATFORMS_BASE = [
  { id: "instagram", color: "bg-pink-500" },
  { id: "twitter", color: "bg-sky-500" },
  { id: "linkedin", color: "bg-blue-700" },
  { id: "facebook", color: "bg-blue-500" },
  { id: "tiktok", color: "bg-gray-900" },
  { id: "youtube", color: "bg-red-600" },
  { id: "threads", color: "bg-gray-700" },
  { id: "pinterest", color: "bg-red-500" },
  { id: "bluesky", color: "bg-blue-400" },
];

const getPlatformLabel = (t: ReturnType<typeof useTranslation>['t'], platformId: string): string => {
  const labels: Record<string, string> = {
    instagram: t('orch_platform_instagram'),
    twitter: t('orch_platform_twitter'),
    linkedin: t('orch_platform_linkedin'),
    facebook: t('orch_platform_facebook'),
    tiktok: t('orch_platform_tiktok'),
    youtube: t('orch_platform_youtube'),
    threads: t('orch_platform_threads'),
    pinterest: t('orch_platform_pinterest'),
    bluesky: t('orch_platform_bluesky'),
  };
  return labels[platformId] || '';
};

const TONES = [
  "engaging",
  "professional",
  "casual",
  "witty",
  "inspirational",
  "educational",
  "trendy",
];

const STEP_LABELS_BASE: Record<string, { icon: unknown }> = {
  strategy: { icon: Target },
  content: { icon: FileText },
  optimization: { icon: Wand2 },
  review: { icon: Star },
  scheduling: { icon: Calendar },
};

const getStepLabel = (t: ReturnType<typeof useTranslation>['t'], stepId: string): string => {
  const labels: Record<string, string> = {
    strategy: t('orch_step_strategy'),
    content: t('orch_step_content'),
    optimization: t('orch_step_optimize'),
    review: t('orch_step_review'),
    scheduling: t('orch_step_schedule'),
  };
  return labels[stepId] || '';
};

// ── Prompt Library (dynamic per flow × wizard step) ────────
//
// These are REAL AI prompts — actual instructions that get injected into the
// AI agent pipeline.  "topic" feeds the Strategy + Content agents;
// "instructions" is appended to EVERY agent's system prompt via the
// `user instructions` field, giving the user fine-grained control over how
// the AI writes, optimizes, and reviews their content.

interface PromptSuggestion {
  title: string;
  /** Goes into the "Topic / Idea" field — the subject the AI writes about */
  topic: string;
  /** Goes into "Extra Instructions" — injected into every AI agent's prompt */
  instructions?: string;
  icon: unknown;
  /** If true, contains [...] placeholders the user should fill in */
  fillIn?: boolean;
  /** Visual tag shown on the card: "topic", "instruction", "strategy" */
  tag?: "topic" | "instruction" | "strategy" | "voice";
}

interface PromptSection {
  id: string;
  label: string;
  icon: unknown;
  color: string;
  /** Which wizard step(s) this section appears in. null = always */
  steps: WizardStep[] | null;
  /** Which flow(s) this section appears in. null = all flows */
  flows: string[] | null;
  prompts: PromptSuggestion[];
}

const ORCHESTRATOR_PROMPT_LIBRARY: PromptSection[] = [

  // ════════════════════════════════════════════════════════
  //  INPUT STEP — Quick Post flow
  // ════════════════════════════════════════════════════════
  {
    id: "quick-topics",
    label: "Quick Post Topics",
    icon: Zap,
    color: "text-amber-500 bg-amber-50",
    steps: ["input"],
    flows: ["quick_post"],
    prompts: [
      {
        title: "Product launch with CTA",
        topic: "Launch of our new AI scheduling feature that saves 5+ hours/week",
        instructions: "Write a scroll-stopping hook in the first line. Include a clear CTA to sign up. Mention the 14-day free trial. Use a confident, excited tone without being salesy. Each platform version should lead with a different benefit.",
        icon: Megaphone,
        tag: "topic",
      },
      {
        title: "Behind-the-scenes story",
        topic: "A day in the life building our startup — the messy reality behind the product",
        instructions: "Write in first-person founder voice. Be vulnerable and specific — share one real moment from today, not generic platitudes. Open with the most surprising or relatable moment. End with a question to spark comments.",
        icon: Eye,
        tag: "topic",
      },
      {
        title: "Data-backed industry take",
        topic: "Why 73% of social media scheduling tools fail their users",
        instructions: "Lead with the statistic as the hook. Structure as: shocking stat → why this happens → what the fix is → CTA. Keep it authoritative but conversational. Use short paragraphs for mobile readability.",
        icon: TrendingUp,
        tag: "topic",
      },
      {
        title: "Customer success spotlight",
        topic: "How [customer name] grew engagement by 300% in 90 days using our platform",
        instructions: "Write as a case study story, not a testimonial. Structure: their problem → what they tried → how they used our tool → specific results with numbers. Make the customer the hero, not us. End with 'DM me to learn how' CTA.",
        icon: Star,
        tag: "topic",
        fillIn: true,
      },
      {
        title: "Quick actionable tip",
        topic: "The one posting schedule change that doubled our Instagram reach this month",
        instructions: "Deliver ONE specific, actionable tip — not a listicle. Open with the result to create curiosity. Explain the exact tactic step by step. End by asking followers to try it and share their results. Keep it under 150 words for Twitter, expand for LinkedIn.",
        icon: Sparkles,
        tag: "topic",
      },
    ],
  },

  // ════════════════════════════════════════════════════════
  //  INPUT STEP — Quick Post AI Instructions
  // ════════════════════════════════════════════════════════
  {
    id: "quick-instructions",
    label: "AI Writing Instructions",
    icon: Wand2,
    color: "text-amber-500 bg-amber-50",
    steps: ["input"],
    flows: ["quick_post"],
    prompts: [
      {
        title: "Engagement-optimized hook",
        topic: "",
        instructions: "Start every platform version with a different scroll-stopping hook. Use pattern interrupts: a controversial statement, a surprising number, or a direct question. Never start with 'We are excited to announce'. The first 5 words determine if people read or scroll past.",
        icon: Target,
        tag: "instruction",
      },
      {
        title: "Story-driven structure",
        topic: "",
        instructions: "Structure the post as a micro-story: situation → tension → resolution → takeaway. Even for short posts, this arc makes content 3x more engaging. Use line breaks between each beat. End with a CTA that flows naturally from the story.",
        icon: FileText,
        tag: "instruction",
      },
      {
        title: "Emoji & formatting rules",
        topic: "",
        instructions: "Use emojis as visual anchors, not decoration — one per key point max. Use line breaks every 1-2 sentences. Bold/caps for ONE key phrase only. No hashtag walls — weave 2-3 relevant hashtags naturally into the text. Format for mobile-first reading.",
        icon: Sparkles,
        tag: "instruction",
      },
    ],
  },

  // ════════════════════════════════════════════════════════
  //  INPUT STEP — Viral Rewriter flow
  // ════════════════════════════════════════════════════════
  {
    id: "viral-topics",
    label: "Viral Topic Angles",
    icon: Flame,
    color: "text-red-500 bg-red-50",
    steps: ["input"],
    flows: ["viral_rewrite"],
    prompts: [
      {
        title: "Contrarian hot take",
        topic: "Unpopular opinion: posting every day is destroying your brand",
        instructions: "Write as a hot take thread. Open with the most controversial version of the statement. Back it up with 2-3 specific reasons. Address the obvious counterargument head-on. End with 'agree or disagree?' to trigger debate. Optimize each platform for maximum reply/share ratio.",
        icon: Flame,
        tag: "topic",
      },
      {
        title: "Myth-busting exposé",
        topic: "5 social media 'best practices' that actually tank your engagement in 2026",
        instructions: "Use numbered myth/truth format. Each myth should be something your audience currently believes. Provide the contrarian truth with a specific example or data point. Write with authority — you're the expert correcting common mistakes. End with 'save this before the algorithm buries it' CTA.",
        icon: XCircle,
        tag: "topic",
      },
      {
        title: "Before/after reveal",
        topic: "Month 1 vs Month 12 of our social media strategy — the uncomfortable truth",
        instructions: "Structure as a transformation story with specific numbers. Be honest about early failures — vulnerability goes viral. Use contrast format: 'Month 1: 23 followers. Month 12: 47,000.' Each point should escalate. End with the ONE thing that changed everything.",
        icon: Eye,
        tag: "topic",
      },
      {
        title: "Trend-jacking reaction",
        topic: "What [trending event/news] teaches us about building an audience in 2026",
        instructions: "React to the trend within the first line — speed matters for virality. Connect the trend to a broader lesson your audience cares about. Take a clear stance — neutral takes don't go viral. Write for quote-retweet/stitch format so people can add their own take.",
        icon: TrendingUp,
        tag: "topic",
        fillIn: true,
      },
    ],
  },
  {
    id: "viral-instructions",
    label: "Viral AI Directives",
    icon: Zap,
    color: "text-red-500 bg-red-50",
    steps: ["input"],
    flows: ["viral_rewrite"],
    prompts: [
      {
        title: "Maximize shareability",
        topic: "",
        instructions: "Optimize every sentence for shareability. Use the 'identity statement' technique — write lines people share because it says something about THEM ('Only real marketers know this'). Create at least one quotable one-liner per platform version. Twitter version should be screenshot-worthy.",
        icon: Flame,
        tag: "instruction",
      },
      {
        title: "Controversy without risk",
        topic: "",
        instructions: "Be boldly opinionated but avoid anything that could damage the brand. The formula: challenge conventional wisdom + back it with evidence + acknowledge the other side briefly + double down on your position. Never punch down. Never be mean. Be provocatively RIGHT.",
        icon: MessageSquare,
        tag: "instruction",
      },
    ],
  },

  // ════════════════════════════════════════════════════════
  //  INPUT STEP — Full Control flow
  // ════════════════════════════════════════════════════════
  {
    id: "full-control-topics",
    label: "Strategic Topics",
    icon: Settings2,
    color: "text-violet-500 bg-violet-50",
    steps: ["input"],
    flows: ["full_control"],
    prompts: [
      {
        title: "Thought leadership article",
        topic: "The future of social media marketing: 5 predictions for the next 3 years based on current data",
        instructions: "Write as a senior strategist making bold predictions. Each prediction must be specific and falsifiable — not vague ('AI will be important'). Support each with a current trend or data point. Structure: prediction → evidence → what to do about it NOW. LinkedIn version should be the most detailed; Twitter should pick the boldest single prediction.",
        icon: Lightbulb,
        tag: "topic",
      },
      {
        title: "Multi-angle campaign",
        topic: "End-of-year sale: 30% off all plans for 48 hours only",
        instructions: "Create a coordinated campaign where each platform hits a different angle. Twitter: urgency and FOMO ('48 hours left'). Instagram: visual lifestyle benefit of upgrading. LinkedIn: ROI case study angle ('$X saved per month'). Facebook: social proof ('Join 10,000+ teams'). Each post should standalone but together they form a surround-sound campaign.",
        icon: Globe,
        tag: "topic",
      },
      {
        title: "Educational deep-dive",
        topic: "Content strategy 101: The exact framework we use to plan, create, and measure social media ROI",
        instructions: "Write as an expert sharing their actual playbook — not generic advice. Include specific frameworks, tools, or processes. Use 'here's exactly what we do' positioning. Break into steps the reader can implement today. Instagram: carousel format. LinkedIn: long-form article. Twitter: thread teaser.",
        icon: FileText,
        tag: "topic",
      },
    ],
  },
  {
    id: "full-control-instructions",
    label: "Quality Control Directives",
    icon: Target,
    color: "text-violet-500 bg-violet-50",
    steps: ["input"],
    flows: ["full_control"],
    prompts: [
      {
        title: "Review-ready quality",
        topic: "",
        instructions: "Every piece of content must be publication-ready before I review it. No placeholder text, no '[insert here]' blanks. Provide specific data points even if estimated. Each platform version must feel like it was hand-crafted by a specialist in that platform, not a generic post truncated to fit.",
        icon: CheckCircle2,
        tag: "instruction",
      },
      {
        title: "Platform-native optimization",
        topic: "",
        instructions: "LinkedIn posts MUST use the first-person professional narrative format. Twitter MUST be a self-contained thought under 250 chars (leave room for engagement). Instagram MUST use line breaks, strategic emojis, and a hook-body-CTA structure. Never just shorten the same text — rewrite from scratch for each platform's culture.",
        icon: Globe,
        tag: "instruction",
      },
    ],
  },

  // ════════════════════════════════════════════════════════
  //  INPUT STEP — Repurpose flow
  // ════════════════════════════════════════════════════════
  {
    id: "repurpose-instructions",
    label: "Repurpose Directives",
    icon: RefreshCw,
    color: "text-teal-500 bg-teal-50",
    steps: ["input"],
    flows: ["repurpose"],
    prompts: [
      {
        title: "Blog → social posts",
        topic: "",
        instructions: "Extract the single most compelling insight from the content. Don't try to summarize everything — pick the one takeaway that would make someone stop scrolling. Rewrite from scratch for each platform using that insight as the hook. Preserve my original voice and perspective but make it punchy.",
        icon: FileText,
        tag: "instruction",
      },
      {
        title: "Long-form → micro-content",
        topic: "",
        instructions: "Break the long-form content into 3-5 standalone micro-posts, each built around one key idea. Each must work independently — no 'as mentioned in part 1'. Twitter: single powerful sentence from each idea. Instagram: expand into a storytelling format. LinkedIn: frame as a professional insight.",
        icon: RefreshCw,
        tag: "instruction",
      },
      {
        title: "Preserve voice, adapt format",
        topic: "",
        instructions: "Keep my original tone, vocabulary, and perspective — do NOT make it sound like generic marketing copy. The adaptation should change structure and length for each platform, but reading it should still feel like ME. Add platform-native elements (hashtags, emojis, hooks) only where they feel natural to my voice.",
        icon: MessageSquare,
        tag: "instruction",
      },
      {
        title: "Newsletter → engagement posts",
        topic: "",
        instructions: "Turn the newsletter content into conversation-starting social posts. Pull out the most debatable or surprising point and frame it as a question or hot take. Structure: bold statement from the newsletter → 'Here's why' or 'Do you agree?' → CTA to read the full newsletter (link in bio).",
        icon: Megaphone,
        tag: "instruction",
      },
    ],
  },

  // ════════════════════════════════════════════════════════
  //  INPUT STEP — Calendar flow
  // ════════════════════════════════════════════════════════
  {
    id: "calendar-strategies",
    label: "Calendar Strategies",
    icon: Calendar,
    color: "text-blue-500 bg-blue-50",
    steps: ["input"],
    flows: ["calendar"],
    prompts: [
      {
        title: "Balanced content mix",
        topic: "Social media marketing tips for small businesses",
        instructions: "Plan a balanced content calendar using the 4-1-1 rule: 4 educational/entertaining posts, 1 soft promotion, 1 hard CTA per cycle. Vary post types across days (text, image, carousel, poll, story). Never schedule two promotional posts back-to-back. Include at least one engagement-bait post (question, poll, 'this or that').",
        icon: Calendar,
        tag: "strategy",
      },
      {
        title: "Launch countdown campaign",
        topic: "Product launch countdown — building anticipation over 7 days",
        instructions: "Day 1-2: mysterious teasers ('Something big is coming'). Day 3-4: reveal one benefit per day. Day 5: social proof / testimonial from beta. Day 6: early access CTA for followers. Day 7: full launch announcement with urgency. Each day should escalate excitement. Use different formats each day (image, video, poll, text).",
        icon: Sparkles,
        tag: "strategy",
      },
      {
        title: "Engagement-first week",
        topic: "Community engagement sprint — maximize replies, saves, and shares this week",
        instructions: "Plan content that prioritizes interaction over impressions. Day 1: controversial opinion poll. Day 2: 'ask me anything' thread. Day 3: user-generated content reshare. Day 4: behind-the-scenes with question. Day 5: 'fill in the blank' prompt. Each post MUST end with a question or interactive element. No pure broadcast posts.",
        icon: Users,
        tag: "strategy",
      },
      {
        title: "Authority building series",
        topic: "Establishing thought leadership in [your niche] over the next 2 weeks",
        instructions: "Build a progressive content arc: week 1 identifies problems your audience faces (empathy posts), week 2 positions you as the solution (expertise posts). Include 1 data-backed insight, 1 personal story, 1 framework/methodology, and 1 prediction per week. LinkedIn gets the deepest versions; Twitter gets the hottest takes.",
        icon: TrendingUp,
        tag: "strategy",
        fillIn: true,
      },
    ],
  },

  // ════════════════════════════════════════════════════════
  //  CONFIGURE STEP — AI Behavior Directives (all flows)
  // ════════════════════════════════════════════════════════
  {
    id: "writing-style",
    label: "Writing Style Directives",
    icon: Wand2,
    color: "text-purple-500 bg-purple-50",
    steps: ["configure"],
    flows: null,
    prompts: [
      {
        title: "Authoritative expert voice",
        topic: "",
        instructions: "Write as a recognized industry expert. Use data, specific examples, and frameworks — never vague claims. Avoid hedging language ('I think', 'maybe'). State opinions as confident assertions backed by evidence. Use 'Here's what most people get wrong' positioning. No exclamation marks — let the content speak for itself.",
        icon: FileText,
        tag: "voice",
      },
      {
        title: "Casual founder voice",
        topic: "",
        instructions: "Write like a founder texting a friend about their business. Use contractions, short sentences, and conversational rhythm. It's OK to start sentences with 'And', 'But', 'Look,'. Share opinions freely. Use humor where it fits naturally. One emoji per post max. Avoid corporate buzzwords entirely — if a Fortune 500 would write it, we won't.",
        icon: MessageSquare,
        tag: "voice",
      },
      {
        title: "Provocative challenger voice",
        topic: "",
        instructions: "Write to challenge the status quo. Open with the most counterintuitive version of our point. Use short, punchy sentences that hit hard. Create 'wait, what?' moments. Back every bold claim with one specific proof point. Write like you're starting a movement, not posting content. End with a line people want to quote-retweet.",
        icon: Flame,
        tag: "voice",
      },
      {
        title: "Educational teacher voice",
        topic: "",
        instructions: "Write as a knowledgeable teacher who genuinely wants the reader to learn. Use 'here's how' and 'step 1, step 2' structure. Include one concrete example for every abstract concept. Anticipate and answer the reader's 'but what about...' objections. End with a specific action the reader can take in the next 5 minutes.",
        icon: Lightbulb,
        tag: "voice",
      },
      {
        title: "Storyteller narrative voice",
        topic: "",
        instructions: "Write every post as a micro-story with a narrative arc. Open with a specific moment in time ('Last Tuesday at 2am, I almost deleted our entire content calendar'). Build tension through the middle. Deliver the insight at the climax. End with the takeaway. Use sensory details and specific numbers — never generic descriptions.",
        icon: Eye,
        tag: "voice",
      },
    ],
  },
  {
    id: "brand-directives",
    label: "Brand Voice Templates",
    icon: Star,
    color: "text-emerald-500 bg-emerald-50",
    steps: ["configure"],
    flows: null,
    prompts: [
      {
        title: "SaaS / tech startup brand",
        topic: "",
        instructions: "Brand voice: innovative, forward-thinking, approachable. Use tech-savvy language but keep it accessible to non-technical audiences. Reference productivity, automation, and 'working smarter'. Avoid jargon dumps. Think Notion or Linear's tone — smart but human. Use lowercase for emphasis over ALL CAPS. Data-driven claims always.",
        icon: Zap,
        tag: "voice",
      },
      {
        title: "D2C / lifestyle brand",
        topic: "",
        instructions: "Brand voice: warm, aspirational, community-first. Use 'you' and 'we' frequently. Paint a picture of the lifestyle our product enables — sell the transformation, not features. Emojis are encouraged but curated (no random emoji spam). Instagram-native visual language. Celebrate customers as heroes. Hashtags should feel like community tags.",
        icon: Star,
        tag: "voice",
      },
      {
        title: "B2B / enterprise brand",
        topic: "",
        instructions: "Brand voice: confident, data-driven, thought-leadership focused. Always include at least one metric, percentage, or specific result. Write for directors and VPs — respect their time and intelligence. LinkedIn is the primary platform — optimize for professional sharing. No emojis on LinkedIn. Use industry terminology accurately. Position as a strategic partner, not a vendor.",
        icon: Globe,
        tag: "voice",
      },
      {
        title: "Creator / personal brand",
        topic: "",
        instructions: "Brand voice: authentic, unfiltered, personality-forward. Write in first person always. Share real opinions even if polarizing. Use your natural speech patterns — imperfect grammar is fine if it sounds like YOU. Reference personal experiences and specific moments. Build parasocial connection through vulnerability and consistency. Every post should feel like it could only come from one person.",
        icon: Users,
        tag: "voice",
      },
    ],
  },
  {
    id: "optimization-directives",
    label: "Optimization Rules",
    icon: Target,
    color: "text-indigo-500 bg-indigo-50",
    steps: ["configure"],
    flows: null,
    prompts: [
      {
        title: "Maximize saves & bookmarks",
        topic: "",
        instructions: "Optimize for saves, not just likes. Include actionable frameworks, checklists, or step-by-step processes people want to reference later. Use 'save this for later' or 'bookmark this' as a natural CTA. Structure content so it provides reference value — lists, numbered steps, templates. Instagram carousel format works best for save-bait.",
        icon: Star,
        tag: "instruction",
      },
      {
        title: "Thread / carousel optimized",
        topic: "",
        instructions: "Structure content for multi-part formats. Twitter: write as a thread with each tweet delivering one complete thought (standalone value). Instagram: carousel slides with one key point per slide, strong title slide, swipe CTA between slides. LinkedIn: use numbered sections with line breaks. Hook in slide/tweet 1 must be irresistible enough to make people swipe/read more.",
        icon: ListChecks,
        tag: "instruction",
      },
      {
        title: "SEO + discoverability focus",
        topic: "",
        instructions: "Front-load keywords in the first 100 characters for every platform. Use hashtags that have high search volume but aren't oversaturated — mix 2 broad + 3 niche per platform. YouTube descriptions: include timestamps, key search terms in first 2 lines. Pinterest: action verbs + specific keywords. Write alt-text friendly descriptions for any image content.",
        icon: Search,
        tag: "instruction",
      },
    ],
  },

  // ════════════════════════════════════════════════════════
  //  INPUT STEP — Universal (all flows)
  // ════════════════════════════════════════════════════════
  {
    id: "universal-instructions",
    label: "General AI Directives",
    icon: Lightbulb,
    color: "text-gray-500 bg-gray-100",
    steps: ["input"],
    flows: null,
    prompts: [
      {
        title: "No corporate speak",
        topic: "",
        instructions: "Never use these words or phrases: 'excited to announce', 'thrilled', 'game-changer', 'synergy', 'leverage', 'at the end of the day', 'it goes without saying'. Write like a human, not a press release. If a sentence sounds like it could be in any company's LinkedIn post, rewrite it until it could only be in OURS.",
        icon: XCircle,
        tag: "instruction",
      },
      {
        title: "Mobile-first formatting",
        topic: "",
        instructions: "Format all content for mobile screens. Max 2 lines per paragraph before a line break. Front-load the most important information — most people won't read past the first 3 lines. Use short sentences (under 15 words). No wide blocks of text. Put the CTA where the thumb naturally scrolls to (not buried at the end of a wall of text).",
        icon: FileText,
        tag: "instruction",
      },
      {
        title: "Engagement-bait CTA",
        topic: "",
        instructions: "End every post with a CTA designed to trigger algorithmic engagement. Best formats: binary choice ('A or B?'), completion prompt ('The biggest mistake in [niche] is ___'), hot take validation ('Agree or disagree?'), experience sharing ('What's your experience with this?'). The CTA should be genuinely interesting, not manipulative.",
        icon: MessageSquare,
        tag: "instruction",
      },
      {
        title: "Specific over generic",
        topic: "",
        instructions: "Replace every generic claim with a specific one. Not 'we grew fast' but 'we went from 0 to 10K users in 47 days'. Not 'great results' but '312% increase in saves'. Not 'many companies' but 'I talked to 14 founders last week and 11 said the same thing'. Specificity is credibility. Vagueness is forgettable.",
        icon: Target,
        tag: "instruction",
      },
    ],
  },
];

// ── Inline Prompt Cards (embedded in wizard steps) ─────────

function InlinePromptCards({
  selectedFlow,
  wizardStep,
  onUsePrompt,
}: {
  selectedFlow: string | null;
  wizardStep: WizardStep;
  onUsePrompt: (topic: string, instructions: string, tag?: string) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [usedKey, setUsedKey] = useState<string | null>(null);

  // Get sections relevant to current flow + step
  const sections = useMemo(() => {
    return ORCHESTRATOR_PROMPT_LIBRARY.filter((sec) => {
      if (sec.steps && !sec.steps.includes(wizardStep)) return false;
      if (sec.flows && selectedFlow && !sec.flows.includes(selectedFlow)) return false;
      return true;
    });
  }, [selectedFlow, wizardStep]);

  // Reset active tab when sections change
  useEffect(() => {
    setActiveTab(0);
  }, [selectedFlow, wizardStep]);

  if (sections.length === 0) return null;

  const currentSection = sections[activeTab] || sections[0];

  const handleUse = (prompt: PromptSuggestion, key: string) => {
    onUsePrompt(prompt.topic, prompt.instructions || "", prompt.tag);
    setUsedKey(key);
    setTimeout(() => setUsedKey(null), 2000);
  };

  const TAG_STYLES: Record<string, string> = {
    topic: "bg-blue-50 text-blue-600",
    instruction: "bg-violet-50 text-violet-600",
    strategy: "bg-amber-50 text-amber-600",
    voice: "bg-emerald-50 text-emerald-600",
  };
  const TAG_LABELS: Record<string, string> = {
    topic: "topic + instructions",
    instruction: "AI directive",
    strategy: "strategy",
    voice: "brand voice",
  };

  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden bg-white">
      {/* Header toggle */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-2.5 px-4 py-3 hover:bg-gray-50 transition-colors"
      >
        <div className="w-7 h-7 rounded-lg bg-violet-50 flex items-center justify-center flex-shrink-0">
          <Sparkles size={14} className="text-white" />
        </div>
        <div className="flex-1 text-left">
          <span className="text-sm font-semibold text-gray-800">Try a Prompt</span>
          <span className="ml-2 text-xs text-gray-400">
            {sections.reduce((a, s) => a + s.prompts.length, 0)} AI prompts for this flow
          </span>
        </div>
        <ChevronDown
          size={16}
          className={`text-gray-400 transition-transform duration-200 ${expanded ? "rotate-180" : ""}`}
        />
      </button>

      {expanded && (
        <div className="border-t border-gray-100">
          {/* Tab bar — one tab per section */}
          {sections.length > 1 && (
            <div className="flex gap-1 px-3 pt-3 pb-1 overflow-x-auto">
              {sections.map((sec, idx) => {
                const SectionIcon = sec.icon as React.ElementType;
                const isActive = idx === activeTab;
                return (
                  <button
                    key={sec.id}
                    onClick={() => setActiveTab(idx)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-colors ${
                      isActive
                        ? "bg-violet-100 text-violet-700"
                        : "text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                    }`}
                  >
                    <SectionIcon size={12} />
                    {sec.label}
                    <span className={`text-[10px] ${isActive ? "text-violet-500" : "text-gray-400"}`}>
                      {sec.prompts.length}
                    </span>
                  </button>
                );
              })}
            </div>
          )}

          {/* Prompt cards grid */}
          <div className="p-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
            {currentSection.prompts.map((prompt, i) => {
              const Icon = prompt.icon as React.ElementType;
              const key = `${currentSection.id}-${i}`;
              const isUsed = usedKey === key;
              const tagStyle = prompt.tag ? TAG_STYLES[prompt.tag] : "";
              const tagLabel = prompt.tag ? TAG_LABELS[prompt.tag] : "";

              return (
                <button
                  key={key}
                  onClick={() => handleUse(prompt, key)}
                  className={`group text-left p-3 rounded-lg border transition-all ${
                    isUsed
                      ? "border-violet-300 bg-violet-50"
                      : "border-gray-150 hover:border-violet-200 hover:bg-violet-50/50 bg-gray-50/50"
                  }`}
                >
                  <div className="flex items-start gap-2">
                    <div className={`w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0 mt-0.5 ${
                      isUsed ? "bg-violet-100" : "bg-white group-hover:bg-violet-100"
                    } transition-colors`}>
                      {isUsed ? (
                        <CheckCircle2 size={12} className="text-violet-500" />
                      ) : (
                        <Icon size={12} className="text-gray-400 group-hover:text-violet-500 transition-colors" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="text-xs font-medium text-gray-800 leading-tight">{prompt.title}</span>
                        {prompt.tag && (
                          <span className={`px-1.5 py-0.5 text-[8px] font-semibold rounded ${tagStyle}`}>
                            {tagLabel}
                          </span>
                        )}
                        {prompt.fillIn && (
                          <span className="px-1.5 py-0.5 text-[8px] font-semibold bg-gray-100 text-gray-500 rounded">
                            edit [...]
                          </span>
                        )}
                      </div>
                      {prompt.topic && (
                        <p className="text-[10px] text-gray-500 mt-1 line-clamp-1 leading-snug">{prompt.topic}</p>
                      )}
                      {prompt.instructions && (
                        <p className="text-[10px] text-violet-400 mt-0.5 line-clamp-2 leading-snug">
                          {prompt.instructions}
                        </p>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Types ──────────────────────────────────────────────────

interface PipelineResult {
  pipeline_id: string;
  pipeline_name?: string;
  status: string;
  quality_score: number;
  approved: boolean;
  platform_content: Record<string, { text: string; hashtags?: string[]; char_count?: number }>;
  review_notes: string[];
  suggested_times: Record<string, string>;
  posts_created: {
    post_id: string;
    text: string;
    platforms: string[];
    quality_score: number;
    scheduled_at?: string;
    status?: string;
    publish_mode?: string;
    preflight_errors?: Array<{ platform?: string; message?: string; code?: string; field?: string }>;
  }[];
  errors: string[];
  cost_usd: number;
  steps_completed: string[];
  current_step: string;
  publish_mode: string;
  // Review loop fields
  review_mode?: string;
  iteration_count?: number;
  iteration_history?: Array<{
    iteration: number;
    score: number;
    notes: string[];
    action: string;
    timestamp?: string;
  }>;
  // Strategist fields
  is_strategist?: boolean;
  strategist_status?: string;
  calendar_plan?: Array<Record<string, unknown>>;
  child_pipeline_ids?: string[];
}

interface PipelineHistoryItem {
  id: string;
  pipeline_name: string;
  topic: string;
  status: string;
  quality_score: number;
  approved: boolean;
  cost_usd: number;
  steps_completed: string[];
  post_ids: string[];
  created_at: string;
  completed_at: string | null;
}

interface PipelineRunDetail extends PipelineHistoryItem {
  target_platforms: string[];
  tone: string;
  instructions: string;
  publish_mode: string;
  content_angle: string;
  post_type: string;
  key_points: string[];
  base_caption: string;
  hashtags: string[];
  platform_content: Record<string, unknown>;
  review_notes: string[];
  suggested_times: Record<string, string>;
  errors: string[];
}

interface SocialAccount {
  id: string;
  platform: string;
  username: string;
  display_name?: string;
  profile_image?: string;
}

interface MediaItem {
  id: string;
  filename: string;
  url: string;
  thumbnail_url?: string;
  media_type: string;
  file_size?: number;
}

// ── Wizard Steps ──────────────────────────────────────────

type WizardStep = "flow" | "input" | "configure" | "processing" | "review" | "publish";

const WIZARD_STEPS: WizardStep[] = ["flow", "input", "configure", "processing", "review", "publish"];

function stepIndex(step: WizardStep) {
  return WIZARD_STEPS.indexOf(step);
}

// ── Component ──────────────────────────────────────────────

export default function OrchestratorPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Wizard state
  const [wizardStep, setWizardStep] = useState<WizardStep>("flow");
  const [activeView, setActiveView] = useState<"wizard" | "history" | "prompts">("wizard");

  // Flow selection
  const [selectedFlow, setSelectedFlow] = useState<string | null>(null);

  // Input step
  const [topic, setTopic] = useState("");
  const [existingText, setExistingText] = useState("");
  const [instructions, setInstructions] = useState("");

  // Configure step
  const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>([]);
  const [selectedAccounts, setSelectedAccounts] = useState<string[]>([]);
  const [tone, setTone] = useState("engaging");
  const [brandVoice, setBrandVoice] = useState("");
  const [selectedMediaIds, setSelectedMediaIds] = useState<string[]>([]);
  const [showMediaPicker, setShowMediaPicker] = useState(false);

  // Review loop settings
  const [reviewMode, setReviewMode] = useState<"auto" | "manual">("auto");

  // Calendar / Strategist specific
  const [calDays, setCalDays] = useState(7);
  const [calCreateDrafts, setCalCreateDrafts] = useState(true);
  const [strategistGoals, setStrategistGoals] = useState("");

  // Publish step
  const [publishMode, setPublishMode] = useState<"draft" | "schedule" | "publish_now">("draft");
  const [customTimes, setCustomTimes] = useState<Record<string, string>>({});

  // Results
  const [result, setResult] = useState<PipelineResult | null>(null);
  const [calResult, setCalResult] = useState<Record<string, unknown> | null>(null);
  const [expandedPlatform, setExpandedPlatform] = useState<string | null>(null);
  // Per-platform edit state — keyed by platform id
  const [editedContent, setEditedContent] = useState<Record<string, { text: string; hashtags: string[] }>>({});
  // Track which platforms were changed by the user vs. left as AI output
  const [editedPlatforms, setEditedPlatforms] = useState<Set<string>>(new Set());
  // Hashtag input buffer per platform
  const [tagInput, setTagInput] = useState<Record<string, string>>({});

  // History
  const [expandedHistoryItem, setExpandedHistoryItem] = useState<string | null>(null);
  const [runDetailCache, setRunDetailCache] = useState<Record<string, PipelineRunDetail>>({});

  // ── Queries ──────────────────────────────────────────────

  const { data: accountsData } = useQuery({
    queryKey: ["social-accounts"],
    queryFn: () => socialAPI.list(),
  });
  const accounts: SocialAccount[] = useMemo(() => (
    Array.isArray(accountsData?.data) ? accountsData.data : (accountsData?.data as Record<string, unknown>)?.accounts || []
  ) as SocialAccount[], [accountsData?.data]);

  const { data: mediaData } = useQuery({
    queryKey: ["media-library"],
    queryFn: () => mediaAPI.list({ limit: 50 }),
    enabled: showMediaPicker,
  });
  const mediaLibrary: MediaItem[] = (() => {
    const raw = mediaData?.data as Record<string, unknown> | unknown[] | undefined;
    if (Array.isArray(raw)) return raw;
    const rawObj = raw as Record<string, unknown> | undefined;
    if (rawObj?.items && Array.isArray(rawObj.items)) return rawObj.items;
    if (rawObj?.media && Array.isArray(rawObj.media)) return rawObj.media;
    return [];
  })();

  const { data: historyData, refetch: refetchHistory } = useQuery({
    queryKey: ["pipeline-history"],
    queryFn: () => orchestratorAPI.history({ limit: 20 }),
    enabled: activeView === "history",
  });
  const history: PipelineHistoryItem[] = (historyData?.data || []) as PipelineHistoryItem[];

  // ── Computed ─────────────────────────────────────────────

  // Build localized FLOWS
  const FLOWS = useMemo(() => FLOWS_BASE.map((f) => ({
    ...f,
    label: getFlowLabel(t, f.id),
    desc: getFlowDesc(t, f.id),
    stepsLabel: getFlowStepsLabel(t, f.id),
  })), [t]);

  // Build localized PLATFORMS
  const PLATFORMS = useMemo(() => PLATFORMS_BASE.map((p) => ({
    ...p,
    label: getPlatformLabel(t, p.id),
  })), [t]);

  // Build localized STEP_LABELS
  const STEP_LABELS = useMemo(() => {
    const labels: Record<string, { label: string; icon: unknown }> = {};
    Object.entries(STEP_LABELS_BASE).forEach(([key, val]) => {
      labels[key] = { ...val, label: getStepLabel(t, key) };
    });
    return labels;
  }, [t]);

  const flow = useMemo(
    () => FLOWS.find((f) => f.id === selectedFlow) || null,
    [FLOWS, selectedFlow],
  );

  const filteredAccounts = useMemo(
    () => accounts.filter((a) => selectedPlatforms.includes(a.platform)),
    [accounts, selectedPlatforms],
  );

  // ── Mutations ────────────────────────────────────────────

  const generateMutation = useMutation({
    mutationFn: () => {
      if (selectedFlow === "repurpose") {
        return orchestratorAPI.optimize({
          text: existingText,
          target_platforms: selectedPlatforms,
          target_account_ids: selectedAccounts.length > 0 ? selectedAccounts : undefined,
          tone,
          brand_voice: brandVoice || undefined,
          media_ids: selectedMediaIds.length > 0 ? selectedMediaIds : undefined,
          publish_mode: publishMode,
          custom_schedule_times: Object.keys(customTimes).length > 0 ? customTimes : undefined,
        });
      }
      return orchestratorAPI.generate({
        topic,
        target_platforms: selectedPlatforms,
        target_account_ids: selectedAccounts.length > 0 ? selectedAccounts : undefined,
        tone,
        brand_voice: brandVoice || undefined,
        media_ids: selectedMediaIds.length > 0 ? selectedMediaIds : undefined,
        instructions: instructions || undefined,
        pipeline: selectedFlow || "quick_post",
        // Full Control pauses after each step — first pause before "content" (after strategy)
        stop_before: flow?.pauseBetweenSteps ? _getFirstPauseStep(selectedFlow || "quick_post") : undefined,
        publish_mode: publishMode,
        custom_schedule_times: Object.keys(customTimes).length > 0 ? customTimes : undefined,
        review_mode: reviewMode,
        max_retries: 2,
      });
    },
    onSuccess: (res) => {
      const data = res.data as PipelineResult;
      setResult(data);
      if (data.status === "completed") {
        setWizardStep("review");
      } else if (data.status === "paused") {
        setWizardStep("review");
        toast.success(t('orch_toast_paused'));
      } else if (data.status === "failed") {
        setWizardStep("review");
        toast.error(`Pipeline failed: ${Array.isArray(data.errors) ? data.errors.join(", ") : "Unknown error"}`);
      }
    },
    onError: (e: unknown) => {
      toast.error(getErrorMessage(e, t('orch_toast_error_generic')));
      setWizardStep("configure");
    },
  });

  const patchContentMutation = useMutation({
    mutationFn: ({ pipelineId, content }: { pipelineId: string; content: Record<string, { text: string; hashtags: string[] }> }) =>
      orchestratorAPI.patchContent(pipelineId, content),
    onSuccess: () => toast.success(t('orch_toast_changes_saved')),
    onError: () => toast.error(t('orch_toast_changes_failed')),
  });

  const resumeMutation = useMutation({
    mutationFn: (args: { pipelineId: string; stepsCompleted?: string[]; pipelineName?: string }) => {
      // Use the ORIGINAL pipeline's flow, not the currently-selected flow
      const origFlowId = args.pipelineName || result?.pipeline_name || selectedFlow || "quick_post";
      const origFlow = FLOWS.find((f) => f.id === origFlowId);
      const allSteps = ["strategy", "content", "optimization", "review", "scheduling"];
      const completedSet = new Set(args.stepsCompleted || result?.steps_completed || []);
      const remaining = allSteps.filter((s) => !completedSet.has(s));
      const nextStop = origFlow?.pauseBetweenSteps && remaining.length > 1 ? remaining[1] : undefined;
      return orchestratorAPI.resume(args.pipelineId, nextStop);
    },
    onSuccess: (res) => {
      const data = res.data as PipelineResult;
      setResult(data);
      if (data.status === "completed") {
        toast.success(t('orch_toast_complete'));
      } else if (data.status === "paused") {
        toast.success(t('orch_toast_next_done'));
      }
    },
    onError: (e: unknown) => {
      const error = e;
      toast.error(getErrorMessage(error, t('orch_toast_resume_failed')));
    },
  });

  const createPostsMutation = useMutation({
    mutationFn: (args: { pipelineId: string; publishMode: string; scheduleTimes?: Record<string, string> }) =>
      orchestratorAPI.createPosts(args.pipelineId, args.publishMode, args.scheduleTimes),
    onSuccess: (res) => {
      const created = ((res.data.posts_created || []) as PipelineResult['posts_created']);
      toast.success(`${created.length} ${t('orch_toast_posts_created')}`);

      // Surface per-post preflight downgrades — the backend saves the
      // post as a draft and attaches the reasons so the user can fix
      // platform requirement mismatches (e.g. TikTok without video)
      // before re-scheduling.
      const downgraded = created.filter(
        (p) => p.preflight_errors && p.preflight_errors.length > 0,
      );
      if (downgraded.length > 0) {
        const first = downgraded[0].preflight_errors?.[0];
        const plat = first?.platform
          ? `${first.platform.charAt(0).toUpperCase()}${first.platform.slice(1)}: `
          : '';
        toast.error(
          t('orch_toast_preflight_downgrade',
            '{{count}} post(s) saved as draft — {{plat}}{{msg}}',
            {
              count: downgraded.length,
              plat,
              msg: first?.message || 'platform requirements not met',
            },
          ),
          { duration: 6000 },
        );
      }

      if (result) {
        setResult({ ...result, posts_created: created });
      }
    },
    onError: (e: unknown) => {
      const error = e;
      toast.error(getErrorMessage(error, t('orch_toast_create_failed')));
    },
  });

  const calendarMutation = useMutation({
    mutationFn: () =>
      orchestratorAPI.calendar({
        topic,
        days: calDays,
        target_platforms: selectedPlatforms,
        target_account_ids: selectedAccounts.length > 0 ? selectedAccounts : undefined,
        tone,
        brand_voice: brandVoice || undefined,
        instructions: instructions || undefined,
        create_drafts: calCreateDrafts,
      }),
    onSuccess: (res) => {
      setCalResult(res.data);
      setWizardStep("review");
      toast.success(
        `Calendar: ${res.data.days} days, ${res.data.total_posts} posts` +
          (res.data.posts_created?.length
            ? ` — ${res.data.posts_created.length} drafts created`
            : ""),
      );
    },
    onError: (e: unknown) => {
      const error = e;
      toast.error(getErrorMessage(error, t('orch_toast_calendar_failed')));
    },
  });

  const strategistMutation = useMutation({
    mutationFn: () =>
      orchestratorAPI.strategist({
        days: calDays,
        target_platforms: selectedPlatforms,
        target_account_ids: selectedAccounts.length > 0 ? selectedAccounts : undefined,
        tone,
        brand_voice: brandVoice || undefined,
        instructions: instructions || undefined,
        goals: strategistGoals || topic || undefined,
        posts_per_day: 1,
        review_mode: reviewMode,
        max_retries: 2,
      }),
    onSuccess: (res) => {
      const data = res.data as PipelineResult;
      setResult(data);
      if (data.status === "completed") {
        setWizardStep("review");
        toast.success(
          t('orch_toast_strategist_done', {
            defaultValue: `Strategist complete: ${(data.child_pipeline_ids || []).length} posts generated`,
          }),
        );
      } else if (data.status === "failed") {
        setWizardStep("review");
        toast.error(`Strategist failed: ${Array.isArray(data.errors) ? data.errors.join(", ") : "Unknown error"}`);
      }
    },
    onError: (e: unknown) => {
      toast.error(getErrorMessage(e, t('orch_toast_error_generic')));
      setWizardStep("configure");
    },
  });

  // ── Helpers ──────────────────────────────────────────────

  const toggleItem = (id: string, list: string[], setter: (v: string[]) => void) => {
    setter(list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(t('orch_toast_copied'));
    } catch {
      toast.error(t('orch_toast_copy_failed'));
    }
  };

  const resetWizard = () => {
    setWizardStep("flow");
    setSelectedFlow(null);
    setTopic("");
    setExistingText("");
    setInstructions("");
    setSelectedPlatforms([]);
    setSelectedAccounts([]);
    setTone("engaging");
    setBrandVoice("");
    setSelectedMediaIds([]);
    setPublishMode("draft");
    setCustomTimes({});
    setResult(null);
    setCalResult(null);
    setExpandedPlatform(null);
    setEditedContent({});
    setEditedPlatforms(new Set());
    setTagInput({});
    setShowMediaPicker(false);
    setReviewMode("auto");
    setStrategistGoals("");
  };

  /** Called when user picks a prompt from the side panel */
  const handleUsePrompt = (promptTopic: string, promptInstructions: string, tag?: string) => {
    if (wizardStep === "input") {
      // Topic prompts fill the topic/text field + instructions
      if (promptTopic) {
        if (flow?.inputType === "text") {
          setExistingText((prev) => prev || promptTopic);
        } else {
          setTopic(promptTopic);
        }
      }
      if (promptInstructions) {
        // If instructions already exist, append with separator
        setInstructions((prev) =>
          prev ? `${prev}\n${promptInstructions}` : promptInstructions,
        );
      }
    } else if (wizardStep === "configure") {
      if (tag === "voice") {
        // Voice/brand prompts go into the brand voice field
        setBrandVoice(promptInstructions);
        toast.success(t('orch_toast_voice_applied'));
        return;
      }
      // Other configure prompts go into instructions
      if (promptInstructions) {
        setInstructions((prev) =>
          prev ? `${prev}\n${promptInstructions}` : promptInstructions,
        );
      }
    }
    toast.success(promptTopic ? t('orch_toast_prompt_applied') : t('orch_toast_directive_applied'));
  };

  // Get the second step in a pipeline (first pause point for Full Control)
  const _getFirstPauseStep = (flowId: string): string | undefined => {
    const FLOW_STEPS: Record<string, string[]> = {
      quick_post: ["strategy", "content", "optimization", "review", "scheduling"],
      full_control: ["strategy", "content", "optimization", "review", "scheduling"],
      repurpose: ["optimization", "review", "scheduling"],
      viral_rewrite: ["strategy", "content", "optimization", "review", "scheduling"],
    };
    const steps = FLOW_STEPS[flowId];
    return steps && steps.length > 1 ? steps[1] : undefined;
  };

  const canProceedFromInput = () => {
    if (selectedFlow === "repurpose") return existingText.trim().length > 0;
    return topic.trim().length > 0;
  };

  const canProceedFromConfigure = () => selectedPlatforms.length > 0;

  const handleRunPipeline = () => {
    setResult(null);
    setCalResult(null);
    setWizardStep("processing");
    if (selectedFlow === "calendar") {
      calendarMutation.mutate();
    } else if (selectedFlow === "strategist") {
      strategistMutation.mutate();
    } else {
      generateMutation.mutate();
    }
  };

  const handleGoToPublish = () => {
    if (flow) setPublishMode(flow.defaultPublishMode);
    setWizardStep("publish");
  };

  const handleFinalPublish = async () => {
    if (!result) {
      toast.error(t('orch_toast_no_result'));
      return;
    }

    if (result.posts_created && result.posts_created.length > 0) {
      // Posts already created — apply publish mode if needed
      if (publishMode === "publish_now") {
        toast.loading(t('orch_toast_publishing'), { id: "publish-now" });
        try {
          await Promise.all(
            result.posts_created.map((p: { post_id?: string }) =>
              p.post_id ? postsAPI.publishNow(p.post_id) : Promise.resolve()
            )
          );
          toast.success(t('orch_toast_published'), { id: "publish-now" });
        } catch (err) {
          toast.error(getErrorMessage(err, t('orch_toast_published_failed')), { id: "publish-now" });
        }
      }
      navigate("/posts");
      return;
    }

    if (result.pipeline_id) {
      // Build schedule times to send
      const schedTimes = publishMode === "schedule" && Object.keys(customTimes).length > 0
        ? customTimes
        : undefined;
      // Create posts from the pipeline with publish mode, then navigate
      createPostsMutation.mutate(
        { pipelineId: result.pipeline_id, publishMode, scheduleTimes: schedTimes },
        {
          onSuccess: () => {
            toast.success(t('orch_toast_create_redirect'));
            navigate("/posts");
          },
        },
      );
    } else {
      toast.error(t('orch_toast_no_result_publish'));
    }
  };

  const statusBadge = (status: string) => {
    const styles: Record<string, string> = {
      completed: "bg-green-100 text-green-700",
      running: "bg-blue-100 text-blue-700",
      paused: "bg-yellow-100 text-yellow-700",
      failed: "bg-red-100 text-red-700",
      pending: "bg-gray-100 text-gray-600",
    };
    return (
      <span
        className={`px-2 py-0.5 rounded-full text-xs font-medium ${styles[status] || styles.pending}`}
      >
        {status}
      </span>
    );
  };

  // ── Progress Indicator ────────────────────────────────────

  const ProgressBar = () => {
    const steps: { key: WizardStep; label: string }[] = [
      { key: "flow", label: "Flow" },
      { key: "input", label: "Input" },
      { key: "configure", label: "Configure" },
      { key: "processing", label: "AI" },
      { key: "review", label: "Review" },
      { key: "publish", label: "Publish" },
    ];
    const currentIdx = stepIndex(wizardStep);

    return (
      <div className="flex items-center gap-1 mb-6">
        {steps.map((s, i) => {
          const isComplete = i < currentIdx;
          const isCurrent = i === currentIdx;
          return (
            <div key={s.key} className="flex items-center gap-1 flex-1">
              <div
                className={`flex items-center justify-center w-8 h-8 rounded-full text-xs font-bold transition-all ${
                  isComplete
                    ? "bg-emerald-500 text-white shadow-sm"
                    : isCurrent
                    ? "bg-violet-600 text-white ring-4 ring-violet-100 shadow-sm"
                    : "bg-gray-100 text-gray-400"
                }`}
              >
                {isComplete ? <Check className="w-4 h-4" /> : i + 1}
              </div>
              <span
                className={`text-[11px] hidden sm:inline font-semibold ${
                  isCurrent ? "text-violet-700" : isComplete ? "text-emerald-600" : "text-gray-400"
                }`}
              >
                {s.label}
              </span>
              {i < steps.length - 1 && (
                <div className={`flex-1 h-[2px] mx-1 rounded-full ${
                  isComplete ? "bg-emerald-400" : "bg-gray-200"
                }`} />
              )}
            </div>
          );
        })}
      </div>
    );
  };

  // ════════════════════════════════════════════════════════════
  //  RENDER
  // ════════════════════════════════════════════════════════════

  return (
    <div className="flex flex-col h-full bg-gray-50">
      {/* Action Bar */}
      <div className="flex items-center justify-between px-5 py-2.5 border-b border-slate-100 bg-white/80 backdrop-blur-md flex-shrink-0 relative before:absolute before:bottom-0 before:left-0 before:right-0 before:h-px before:bg-gradient-to-r before:from-transparent before:via-violet-100 before:to-transparent z-10">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-violet-400 to-violet-600 flex items-center justify-center shadow-sm shadow-violet-200/50">
            <Workflow size={15} className="text-white drop-shadow-sm" />
          </div>
          <span className="text-sm font-bold text-gray-900 tracking-tight">{t('orch_title_orchestrator')}</span>
        </div>
        <div className="flex items-center gap-1 bg-gray-50 rounded-xl p-1 border border-gray-100">
          {activeView !== "wizard" || wizardStep !== "flow" ? (
            <button
              onClick={() => {
                setActiveView("wizard");
                resetWizard();
              }}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                activeView === "wizard"
                  ? "bg-white text-violet-700 shadow-sm"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              <Sparkles className="w-3.5 h-3.5" />
              {t('orch_btn_new')}
            </button>
          ) : null}
          <button
            onClick={() => {
              setActiveView("history");
              refetchHistory();
            }}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
              activeView === "history"
                ? "bg-white text-violet-700 shadow-sm"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            <Clock className="w-3.5 h-3.5" />
            {t('orch_btn_history')}
          </button>
          <button
            onClick={() => setActiveView("prompts")}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
              activeView === "prompts"
                ? "bg-white text-violet-700 shadow-sm"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            <Settings2 className="w-3.5 h-3.5" />
            {t('orch_btn_prompts')}
          </button>
        </div>
      </div>

      {/* ═══════════ WIZARD VIEW ═══════════ */}
      {activeView === "wizard" && (
        <div className="flex-1 overflow-y-auto px-4 py-6">
          <div className="max-w-3xl mx-auto">

          {wizardStep !== "flow" && <ProgressBar />}

          {/* ── Step 1: Flow Selection (Agent-style hero) ───── */}
          {wizardStep === "flow" && (
            <div className="flex flex-col items-center justify-center py-8">
              {/* Hero icon with glow */}
              <div className="relative mb-6 animate-fade-in-up" style={{ animationDelay: '0.05s' }}>
                <div className="absolute inset-0 rounded-3xl bg-violet-400/20 blur-xl scale-150" />
                <div className="relative w-16 h-16 rounded-2xl bg-gradient-to-br from-violet-400 to-violet-600 flex items-center justify-center shadow-lg shadow-violet-200/50">
                  <Workflow size={28} className="text-white drop-shadow-sm" />
                </div>
              </div>

              <h2 className="text-2xl font-extrabold text-gray-900 mb-2 tracking-tight animate-fade-in-up" style={{ animationDelay: '0.1s' }}>{t('orch_heading_what_create')}</h2>
              <p className="text-sm text-gray-400 text-center mb-10 max-w-md leading-relaxed animate-fade-in-up" style={{ animationDelay: '0.15s' }}>
                {t('orch_desc_flow_selection')}
              </p>

              {/* Flow Cards */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 w-full max-w-2xl">
                {FLOWS.map((f, i) => {
                  const FlowIcon = f.icon as React.ElementType;
                  return (
                  <button
                    key={f.id}
                    onClick={() => {
                      setSelectedFlow(f.id);
                      if (f.defaultPublishMode) setPublishMode(f.defaultPublishMode);
                      setWizardStep("input");
                    }}
                    className="card-glow group flex flex-col items-start gap-3 p-5 bg-white/80 backdrop-blur-sm border border-slate-200/60 rounded-2xl hover:border-violet-200 hover:-translate-y-1 transition-all duration-300 text-left relative overflow-hidden animate-fade-in-up"
                    style={{ animationDelay: `${0.2 + i * 0.04}s`, boxShadow: '0 1px 3px 0 rgba(0,0,0,0.02), 0 1px 2px -1px rgba(0,0,0,0.02)' }}
                  >
                    <div className="absolute inset-0 bg-gradient-to-br from-violet-50/0 to-violet-50/0 group-hover:from-violet-50/50 group-hover:to-purple-50/30 transition-all duration-300" />
                    <div className={`relative w-11 h-11 rounded-xl bg-gradient-to-br ${f.color} flex items-center justify-center flex-shrink-0 shadow-sm group-hover:scale-110 group-hover:shadow-md transition-all duration-300`}>
                      <FlowIcon className="w-5 h-5 text-white drop-shadow-sm" />
                    </div>
                    <div className="relative min-w-0">
                      <span className="text-sm font-bold text-gray-800 leading-tight block group-hover:text-gray-900">
                        {f.label}
                      </span>
                      <span className="text-[11px] text-gray-400 leading-snug block mt-0.5">{f.desc}</span>
                    </div>
                  </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── Step 2: Input ─────────────────────────────── */}
          {wizardStep === "input" && flow && (
            <div className="space-y-5">
              <div className="flex items-center gap-3 mb-2">
                <div className={`w-9 h-9 rounded-xl bg-gradient-to-br ${flow.color} flex items-center justify-center text-white shadow-sm`}>
                  {(() => {
                    const FlowIcon = flow.icon as React.ElementType;
                    return <FlowIcon className="w-4.5 h-4.5" />;
                  })()}
                </div>
                <div>
                  <h2 className="text-sm font-bold text-gray-900">{flow.label}</h2>
                  <p className="text-[11px] text-gray-400">{flow.stepsLabel}</p>
                </div>
              </div>

              <div className="bg-white rounded-2xl border border-slate-200/60 p-5 space-y-4 shadow-sm shadow-gray-100/30">
                {flow.inputType === "text" ? (
                  <>
                    <label className="text-xs font-bold text-gray-600 uppercase tracking-wider">
                      {t('orch_label_existing_content')}
                    </label>
                    <textarea
                      value={existingText}
                      onChange={(e) => setExistingText(e.target.value)}
                      placeholder={t('orch_placeholder_existing_content')}
                      rows={6}
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-violet-300/50 focus:border-violet-200 focus:shadow-md focus:shadow-violet-100/30 resize-none bg-gray-50/50 focus:bg-white transition-all duration-200"
                    />
                  </>
                ) : (
                  <>
                    <label className="text-xs font-bold text-gray-600 uppercase tracking-wider">
                      {selectedFlow === "viral_rewrite"
                        ? t('orch_label_trending_topic')
                        : selectedFlow === "calendar"
                        ? t('orch_label_content_theme')
                        : t('orch_label_topic')}
                    </label>
                    <textarea
                      value={topic}
                      onChange={(e) => setTopic(e.target.value)}
                      placeholder={
                        selectedFlow === "viral_rewrite"
                          ? "e.g., AI replacing creative jobs — hot take from both sides"
                          : selectedFlow === "calendar"
                          ? "e.g., Social media marketing tips for small businesses"
                          : "e.g., Launch of our new AI scheduling feature that saves 5+ hours/week"
                      }
                      rows={5}
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-violet-300/50 focus:border-violet-200 focus:shadow-md focus:shadow-violet-100/30 resize-none bg-gray-50/50 focus:bg-white transition-all duration-200"
                    />
                    <label className="text-xs font-bold text-gray-600 uppercase tracking-wider">
                      {t('orch_label_extra_instructions')} <span className="text-gray-400 font-normal normal-case">{t('orch_label_extra_instructions_optional')}</span>
                    </label>
                    <input
                      value={instructions}
                      onChange={(e) => setInstructions(e.target.value)}
                      placeholder={t('orch_placeholder_extra_instructions')}
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-violet-300/50 focus:border-violet-200 bg-gray-50/50 focus:bg-white transition-all duration-200"
                    />
                  </>
                )}

                {/* Calendar-specific: days selector */}
                {(selectedFlow === "calendar" || selectedFlow === "strategist") && (
                  <>
                    <label className="text-sm font-semibold text-gray-700">{t('orch_label_plan_duration')}</label>
                    <div className="flex gap-2">
                      {[3, 5, 7, 14, 30].map((d) => (
                        <button
                          key={d}
                          onClick={() => setCalDays(d)}
                          className={`px-3 py-1.5 rounded-lg text-xs font-medium ${
                            calDays === d
                              ? "bg-violet-600 text-white"
                              : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                          }`}
                        >
                          {d} {t('orch_days_suffix')}
                        </button>
                      ))}
                    </div>
                    {selectedFlow === "calendar" && (
                      <label className="flex items-center gap-2 text-sm text-gray-700">
                        <input
                          type="checkbox"
                          checked={calCreateDrafts}
                          onChange={(e) => setCalCreateDrafts(e.target.checked)}
                          className="rounded border-gray-300"
                        />
                        {t('orch_label_create_drafts')}
                      </label>
                    )}
                  </>
                )}
              </div>

              {/* ── Inline Prompt Suggestions ── */}
              <InlinePromptCards
                selectedFlow={selectedFlow}
                wizardStep="input"
                onUsePrompt={handleUsePrompt}
              />

              <div className="flex justify-between items-center pt-4 mt-4 border-t border-gray-100">
                <button
                  onClick={() => {
                    setWizardStep("flow");
                    setSelectedFlow(null);
                  }}
                  className="flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium text-gray-600 hover:text-gray-800 rounded-xl hover:bg-gray-100 transition-colors"
                >
                  <ArrowLeft className="w-4 h-4" /> {t('orch_btn_back')}
                </button>
                <button
                  onClick={() => setWizardStep("configure")}
                  disabled={!canProceedFromInput()}
                  className="flex items-center gap-1.5 px-6 py-2.5 bg-gradient-to-r from-violet-500 to-violet-600 text-white rounded-xl text-sm font-bold hover:shadow-md hover:shadow-violet-200/50 disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200 shadow-sm active:scale-95"
                >
                  {t('orch_btn_next_configure')} <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {/* ── Step 3: Configure ─────────────────────────── */}
          {wizardStep === "configure" && flow && (
            <div className="space-y-5">
              <h2 className="text-base font-bold text-gray-900 tracking-tight">{t('orch_heading_configure')}</h2>

              {/* Platforms */}
              <div className="bg-white rounded-2xl border border-slate-200/60 p-5 space-y-3 shadow-sm shadow-gray-100/30">
                <label className="text-xs font-bold text-gray-600 uppercase tracking-wider">{t('orch_label_target_platforms')}</label>
                <div className="flex flex-wrap gap-2">
                  {PLATFORMS.map((p) => (
                    <button
                      key={p.id}
                      onClick={() => toggleItem(p.id, selectedPlatforms, setSelectedPlatforms)}
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${
                        selectedPlatforms.includes(p.id)
                          ? `${p.color} text-white shadow-sm`
                          : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                      }`}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Accounts */}
              {filteredAccounts.length > 0 && (
                <div className="bg-white/80 backdrop-blur-sm rounded-2xl border border-gray-100 p-5 space-y-3 shadow-sm shadow-gray-100/80">
                  <label className="text-sm font-semibold text-gray-700 flex items-center gap-1.5">
                    <Users className="w-4 h-4 text-gray-400" /> {t('orch_label_post_to_accounts')}
                  </label>
                  <div className="space-y-2">
                    {filteredAccounts.map((acc) => {
                      const platInfo = PLATFORMS.find((p) => p.id === acc.platform);
                      return (
                        <button
                          key={acc.id}
                          onClick={() =>
                            toggleItem(acc.id, selectedAccounts, setSelectedAccounts)
                          }
                          className={`w-full flex items-center gap-3 p-2.5 rounded-lg border text-left transition-all ${
                            selectedAccounts.includes(acc.id)
                              ? "border-violet-500 bg-violet-50"
                              : "border-gray-200 hover:border-gray-300"
                          }`}
                        >
                          <div
                            className={`w-2 h-2 rounded-full ${platInfo?.color || "bg-gray-400"}`}
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-800 truncate">
                              {acc.display_name || acc.username}
                            </p>
                            <p className="text-xs text-gray-500">
                              {platInfo?.label} · @{acc.username}
                            </p>
                          </div>
                          {selectedAccounts.includes(acc.id) && (
                            <Check className="w-4 h-4 text-violet-600" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Tone + Brand Voice */}
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl border border-gray-100 p-5 space-y-3 shadow-sm shadow-gray-100/80">
                <label className="text-xs font-bold text-gray-600 uppercase tracking-wider">{t('orch_label_tone')}</label>
                <div className="flex flex-wrap gap-2">
                  {TONES.map((toneVal) => (
                    <button
                      key={toneVal}
                      onClick={() => setTone(toneVal)}
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold capitalize transition-all ${
                        tone === toneVal
                          ? "bg-violet-600 text-white shadow-sm"
                          : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                      }`}
                    >
                      {toneVal}
                    </button>
                  ))}
                </div>
                {flow.inputType !== "text" && (
                  <>
                    <label className="text-sm font-semibold text-gray-700 mt-3 block">
                      {t('orch_label_brand_voice')}
                    </label>
                    <input
                      value={brandVoice}
                      onChange={(e) => setBrandVoice(e.target.value)}
                      placeholder={t('orch_placeholder_brand_voice')}
                      className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm bg-gray-50/50 focus:bg-white focus:ring-2 focus:ring-violet-300/50 focus:border-violet-200 transition-all duration-200"
                    />
                  </>
                )}
              </div>

              {/* Media Picker */}
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl border border-gray-100 p-5 space-y-3 shadow-sm shadow-gray-100/80">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-semibold text-gray-700 flex items-center gap-1.5">
                    <Image className="w-4 h-4 text-gray-400" /> {t('orch_label_attach_media')}
                  </label>
                  <button
                    onClick={() => setShowMediaPicker(!showMediaPicker)}
                    className="text-xs text-violet-600 hover:text-violet-800"
                  >
                    {showMediaPicker ? t('orch_btn_hide') : t('orch_btn_browse_library')}
                  </button>
                </div>
                {selectedMediaIds.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {selectedMediaIds.map((mid) => (
                      <span
                        key={mid}
                        className="inline-flex items-center gap-1 px-2 py-0.5 bg-violet-100 text-violet-700 rounded text-xs"
                      >
                        {mid.slice(0, 8)}...
                        <button
                          onClick={() =>
                            setSelectedMediaIds(selectedMediaIds.filter((m) => m !== mid))
                          }
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                {showMediaPicker && (
                  <div className="grid grid-cols-4 gap-2 max-h-48 overflow-y-auto">
                    {mediaLibrary.map((m) => (
                      <button
                        key={m.id}
                        onClick={() => toggleItem(m.id, selectedMediaIds, setSelectedMediaIds)}
                        className={`relative rounded-lg overflow-hidden border-2 transition-all ${
                          selectedMediaIds.includes(m.id)
                            ? "border-violet-500"
                            : "border-transparent hover:border-gray-300"
                        }`}
                      >
                        <img
                          src={m.thumbnail_url || m.url}
                          alt={m.filename}
                          className="w-full h-20 object-cover"
                          onError={(e) => {
                            const target = e.currentTarget;
                            target.style.display = "none";
                            const fallback = target.nextElementSibling as HTMLElement;
                            if (fallback) fallback.style.display = "flex";
                          }}
                        />
                        <div className="w-full h-20 bg-gray-100 items-center justify-center hidden">
                          <Image className="w-6 h-6 text-gray-300" />
                        </div>
                        {selectedMediaIds.includes(m.id) && (
                          <div className="absolute top-1 right-1 w-5 h-5 bg-violet-600 rounded-full flex items-center justify-center">
                            <Check className="w-3 h-3 text-white" />
                          </div>
                        )}
                        <p className="text-[10px] text-gray-500 truncate px-1">{m.filename}</p>
                      </button>
                    ))}
                    {mediaLibrary.length === 0 && (
                      <p className="col-span-4 text-xs text-gray-400 text-center py-4">
                        {t('orch_text_no_media')}
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Full Control note */}
              {flow.pauseBetweenSteps && (
                <div className="bg-amber-50 rounded-xl border border-amber-200 p-4">
                  <p className="text-sm font-medium text-amber-800 mb-1">
                    {t('orch_note_full_control')}
                  </p>
                  <p className="text-xs text-amber-600">
                    {t('orch_note_full_control_desc')}
                  </p>
                </div>
              )}

              {/* ── Review Mode Toggle ── */}
              {selectedFlow !== "calendar" && (
                <div className="bg-gradient-to-r from-gray-50 to-violet-50/30 rounded-xl border border-gray-100 p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-sm font-semibold text-gray-800">
                        {t('orch_review_loop_title', 'Review Feedback Loop')}
                      </h4>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {reviewMode === "auto"
                          ? t('orch_review_auto_desc', 'AI auto-retries when quality score is low (up to 2 iterations)')
                          : t('orch_review_manual_desc', 'Pipeline pauses for your review when quality is flagged')}
                      </p>
                    </div>
                    <div className="flex items-center bg-gray-100 rounded-lg p-0.5">
                      <button
                        onClick={() => setReviewMode("auto")}
                        className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
                          reviewMode === "auto"
                            ? "bg-white text-violet-700 shadow-sm"
                            : "text-gray-500 hover:text-gray-700"
                        }`}
                      >
                        <span className="flex items-center gap-1">
                          <RotateCcw className="w-3 h-3" />
                          {t('orch_review_auto', 'Auto')}
                        </span>
                      </button>
                      <button
                        onClick={() => setReviewMode("manual")}
                        className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
                          reviewMode === "manual"
                            ? "bg-white text-orange-700 shadow-sm"
                            : "text-gray-500 hover:text-gray-700"
                        }`}
                      >
                        <span className="flex items-center gap-1">
                          <Pause className="w-3 h-3" />
                          {t('orch_review_manual', 'Manual')}
                        </span>
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* ── Strategist Goals (only for strategist flow) ── */}
              {selectedFlow === "strategist" && (
                <div>
                  <label htmlFor="strategist-goals" className="block text-xs font-semibold text-gray-700 mb-1.5">
                    {t('orch_strategist_goals', 'Content Goals')}
                  </label>
                  <textarea
                    id="strategist-goals"
                    value={strategistGoals}
                    onChange={(e) => setStrategistGoals(e.target.value)}
                    placeholder={t('orch_strategist_goals_placeholder', 'e.g. Grow engagement, launch new product line, build thought leadership...')}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-violet-200 focus:border-violet-400 outline-none resize-none placeholder:text-gray-400"
                  />
                </div>
              )}

              {/* ── Inline Prompt Suggestions (voice/brand/optimization) ── */}
              <InlinePromptCards
                selectedFlow={selectedFlow}
                wizardStep="configure"
                onUsePrompt={handleUsePrompt}
              />

              <div className="flex justify-between items-center pt-4 mt-4 border-t border-gray-200">
                <button
                  onClick={() => setWizardStep("input")}
                  className="flex items-center gap-1.5 px-4 py-2.5 text-sm text-gray-600 hover:text-gray-800 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <ArrowLeft className="w-4 h-4" /> Back
                </button>
                <button
                  onClick={handleRunPipeline}
                  disabled={!canProceedFromConfigure()}
                  className="flex items-center gap-1.5 px-6 py-2.5 bg-gradient-to-r from-violet-500 to-violet-600 text-white rounded-xl text-sm font-bold hover:shadow-md hover:shadow-violet-200/50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-sm active:scale-95"
                >
                  <Play className="w-4 h-4" />
                  {selectedFlow === "calendar"
                    ? t('orch_btn_run_calendar')
                    : selectedFlow === "strategist"
                    ? t('orch_btn_run_strategist', 'Launch Strategist')
                    : t('orch_btn_run_pipeline')}
                </button>
              </div>
            </div>
          )}

          {/* ── Step 4: Processing (Agent Pipeline Graph) ── */}
          {wizardStep === "processing" && flow && (
            <div className="max-w-2xl mx-auto">
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl border border-gray-100 p-6 shadow-sm shadow-gray-100/80">
                <div className="flex items-center gap-3 mb-4">
                  <div className="relative w-10 h-10">
                    <div className="absolute inset-0 rounded-xl bg-violet-400/20 blur-lg scale-150 animate-pulse" />
                    <div className="relative w-10 h-10 rounded-xl bg-gradient-to-br from-violet-400 to-violet-600 flex items-center justify-center shadow-lg shadow-violet-200/50">
                      <Loader2 className="w-5 h-5 text-white animate-spin" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-gray-900">{t('orch_text_processing')}</h3>
                    <p className="text-xs text-gray-400">{flow.label} {t('orch_desc_processing')}</p>
                  </div>
                </div>

                {/* Live Agent Pipeline Graph */}
                {selectedFlow === "strategist" && result ? (
                  <ContentStrategistFlow
                    strategistStatus={result.strategist_status || "planning"}
                    calendarPlan={(result.calendar_plan || []) as Array<{ day: number; theme?: string; posts?: Array<{ platform?: string; topic?: string; caption?: string; hook?: string; post_type?: string }> }>}
                    childPipelines={[]}
                    qualityScore={result.quality_score}
                    costUsd={result.cost_usd}
                    errors={result.errors}
                  />
                ) : (
                  <AgentPipelineGraph
                    stepsCompleted={result?.steps_completed || []}
                    currentStep={result?.current_step || "strategy"}
                    status={result?.status || "running"}
                    iterationCount={result?.iteration_count ?? 0}
                    iterationHistory={result?.iteration_history || []}
                    targetPlatforms={selectedPlatforms}
                    qualityScore={result?.quality_score ?? 0}
                    reviewMode={reviewMode}
                  />
                )}
              </div>
            </div>
          )}

          {/* ── Step 5: Review & Edit ─────────────────────── */}
          {wizardStep === "review" && (
            <div className="max-w-3xl mx-auto space-y-5">
              <h2 className="text-sm font-bold text-gray-900">Review Results</h2>

              {/* Agent Pipeline Graph (shows iteration history) */}
              {result && (result.iteration_count ?? 0) > 0 && (
                <AgentPipelineGraph
                  stepsCompleted={result.steps_completed}
                  currentStep={result.current_step}
                  status={result.status}
                  iterationCount={result.iteration_count ?? 0}
                  iterationHistory={result.iteration_history || []}
                  targetPlatforms={selectedPlatforms}
                  qualityScore={result.quality_score}
                  reviewMode={reviewMode}
                />
              )}

              {/* Strategist Summary (if strategist flow) */}
              {result && result.is_strategist && (
                <ContentStrategistFlow
                  strategistStatus={result.strategist_status || "completed"}
                  calendarPlan={(result.calendar_plan || []) as Array<{ day: number; theme?: string; posts?: Array<{ platform?: string; topic?: string; caption?: string; hook?: string; post_type?: string }> }>}
                  childPipelines={[]}
                  qualityScore={result.quality_score}
                  costUsd={result.cost_usd}
                  errors={result.errors}
                />
              )}

              {/* Pipeline Result */}
              {result && (
                <>
                  {/* Status Card */}
                  <div className="bg-white/80 backdrop-blur-sm rounded-2xl border border-gray-100 p-5 shadow-sm shadow-gray-100/80">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        {result.status === "completed" && result.approved && (
                          <CheckCircle2 className="w-6 h-6 text-green-500" />
                        )}
                        {result.status === "completed" && !result.approved && (
                          <Eye className="w-6 h-6 text-yellow-500" />
                        )}
                        {result.status === "paused" && (
                          <Pause className="w-6 h-6 text-yellow-500" />
                        )}
                        {result.status === "failed" && (
                          <XCircle className="w-6 h-6 text-red-500" />
                        )}
                        <div>
                          <h3 className="font-semibold text-gray-900">
                            {result.status === "completed" && result.approved
                              ? "Pipeline Complete — Approved!"
                              : result.status === "completed"
                              ? "Complete — Needs Review"
                              : result.status === "paused"
                              ? "Paused for Your Review"
                              : "Pipeline Failed"}
                          </h3>
                          <p className="text-xs text-gray-500">
                            {result.steps_completed.length} steps done
                            {result.cost_usd > 0 && ` · $${result.cost_usd.toFixed(4)} AI cost`}
                          </p>
                        </div>
                      </div>
                      {statusBadge(result.status)}
                    </div>

                    {/* Steps Progress */}
                    <div className="flex items-center gap-1 mb-4">
                      {Object.keys(STEP_LABELS).map((step) => {
                        const completed = result.steps_completed.includes(step);
                        const current = result.current_step === step;
                        return (
                          <div key={step} className="flex-1 flex flex-col items-center gap-1">
                            <div
                              className={`w-full h-2 rounded-full ${
                                completed ? "bg-green-500" : current ? "bg-yellow-400" : "bg-gray-200"
                              }`}
                            />
                            <span className="text-[10px] text-gray-500">
                              {STEP_LABELS[step]?.label}
                            </span>
                          </div>
                        );
                      })}
                    </div>

                    {/* Quality Score */}
                    {result.quality_score > 0 && (
                      <div className="flex items-center gap-2 mb-3">
                        <Star className="w-4 h-4 text-yellow-500" />
                        <span className="text-sm font-medium">
                          Quality: {result.quality_score}/10
                        </span>
                        <div className="flex-1 bg-gray-200 rounded-full h-2 ml-2">
                          <div
                            className={`h-2 rounded-full ${
                              result.quality_score >= 7
                                ? "bg-green-500"
                                : result.quality_score >= 5
                                ? "bg-yellow-500"
                                : "bg-red-500"
                            }`}
                            style={{ width: `${result.quality_score * 10}%` }}
                          />
                        </div>
                      </div>
                    )}

                    {/* Review Notes */}
                    {result.review_notes.length > 0 && (
                      <div className="bg-gray-50 rounded-lg p-3 mb-3">
                        <p className="text-xs font-semibold text-gray-600 mb-1">Review Notes:</p>
                        <ul className="space-y-1">
                          {result.review_notes.map((note, i) => (
                            <li
                              key={i}
                              className="text-xs text-gray-600 flex items-start gap-1.5"
                            >
                              <ListChecks className="w-3 h-3 mt-0.5 text-violet-500 flex-shrink-0" />
                              {note}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Errors */}
                    {result.errors.length > 0 && (
                      <div className="bg-red-50 rounded-lg p-3 mb-3">
                        {result.errors.map((err, i) => (
                          <p key={i} className="text-xs text-red-600">
                            {err}
                          </p>
                        ))}
                      </div>
                    )}

                    {/* Resume / Retry */}
                    {(result.status === "paused" || result.status === "failed") && (
                      <button
                        onClick={() => resumeMutation.mutate({ pipelineId: result.pipeline_id, stepsCompleted: result.steps_completed, pipelineName: result.pipeline_name })}
                        disabled={resumeMutation.isPending}
                        className={`flex items-center gap-1.5 px-4 py-2 text-white rounded-lg text-sm font-medium ${
                          result.status === "failed"
                            ? "bg-orange-600 hover:bg-orange-700"
                            : "bg-violet-600 hover:bg-violet-700"
                        }`}
                      >
                        {resumeMutation.isPending ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : result.status === "failed" ? (
                          <RotateCcw className="w-3.5 h-3.5" />
                        ) : (
                          <Play className="w-3.5 h-3.5" />
                        )}
                        {result.status === "failed" ? "Retry" : "Continue Pipeline"}
                      </button>
                    )}
                  </div>

                  {/* Platform Content Cards */}
                  {Object.keys(result.platform_content).length === 0 && result.status === "completed" && (
                    <div className="bg-amber-50 rounded-xl border border-amber-200 p-4 text-center">
                      <AlertCircle className="w-8 h-8 text-amber-400 mx-auto mb-2" />
                      <p className="text-sm font-medium text-amber-800">No platform content generated</p>
                      <p className="text-xs text-amber-600 mt-1">The pipeline completed but didn&apos;t produce platform-specific content. Try running again with a clearer topic or different platforms.</p>
                    </div>
                  )}
                  {Object.keys(result.platform_content).length > 0 && (
                    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                      <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
                        <h3 className="font-semibold text-gray-900 text-sm">
                          Platform-Optimized Content
                        </h3>
                        <span className="text-[11px] text-gray-400">Click a platform to expand and edit</span>
                      </div>
                      <div className="divide-y divide-gray-100">
                        {Object.entries(result.platform_content).map(([platform, content]) => {
                          const platInfo = PLATFORMS.find((p) => p.id === platform);
                          const isExpanded = expandedPlatform === platform;
                          const suggestedTime = result.suggested_times[platform];
                          const isEdited = editedPlatforms.has(platform);

                          // Use user-edited content if present, else AI output
                          const liveText = editedContent[platform]?.text ?? content.text ?? '';
                          const liveHashtags = editedContent[platform]?.hashtags ?? content.hashtags ?? [];

                          // Platform char limits
                          const LIMITS: Record<string, number> = {
                            twitter: 280, bluesky: 300, threads: 500,
                            instagram: 2200, tiktok: 2200, linkedin: 3000,
                            facebook: 63206, youtube: 5000,
                          };
                          const limit = LIMITS[platform];
                          const charCount = liveText.length + (liveHashtags.length ? liveHashtags.join(' ').length + 1 : 0);
                          const isOverLimit = limit ? charCount > limit : false;

                          return (
                            <div key={platform}>
                              {/* Collapsed header */}
                              <button
                                onClick={() => {
                                  if (!isExpanded) {
                                    // Seed edit state from AI output if not yet edited
                                    if (!editedContent[platform]) {
                                      setEditedContent(prev => ({
                                        ...prev,
                                        [platform]: { text: content.text || '', hashtags: content.hashtags || [] },
                                      }));
                                    }
                                  }
                                  setExpandedPlatform(isExpanded ? null : platform);
                                }}
                                className="w-full flex items-center gap-3 px-5 py-3 hover:bg-gray-50 transition-colors"
                              >
                                <div className={`w-2 h-2 rounded-full ${platInfo?.color || 'bg-gray-400'}`} />
                                <span className="font-medium text-sm text-gray-800">
                                  {platInfo?.label || platform}
                                </span>
                                {isEdited && (
                                  <span className="flex items-center gap-1 text-[10px] px-1.5 py-0.5 bg-amber-50 text-amber-600 rounded-full border border-amber-200 font-semibold">
                                    <Pencil className="w-2.5 h-2.5" /> Edited
                                  </span>
                                )}
                                {suggestedTime && (
                                  <span className="text-[10px] text-gray-400 ml-1">
                                    <Clock className="w-3 h-3 inline" />{' '}
                                    {new Date(suggestedTime).toLocaleString()}
                                  </span>
                                )}
                                <span className={`text-xs ml-auto mr-2 font-mono ${
                                  isOverLimit ? 'text-red-500 font-bold' : 'text-gray-400'
                                }`}>
                                  {charCount}{limit ? `/${limit}` : ''}
                                </span>
                                {isExpanded ? (
                                  <ChevronUp className="w-4 h-4 text-gray-400" />
                                ) : (
                                  <ChevronDown className="w-4 h-4 text-gray-400" />
                                )}
                              </button>

                              {/* Expanded edit panel */}
                              {isExpanded && (
                                <div className="px-5 pb-5 space-y-3 bg-gray-50/50 border-t border-gray-100">
                                  {/* Caption textarea */}
                                  <div className="pt-3">
                                    <div className="flex items-center justify-between mb-1.5">
                                      <label className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide">Caption</label>
                                      <span className={`text-[11px] font-mono ${
                                        isOverLimit ? 'text-red-500 font-bold' : 'text-gray-400'
                                      }`}>
                                        {charCount}{limit ? ` / ${limit}` : ''} chars
                                      </span>
                                    </div>
                                    <textarea
                                      value={liveText}
                                      onChange={(e) => {
                                        const val = e.target.value;
                                        setEditedContent(prev => ({
                                          ...prev,
                                          [platform]: { text: val, hashtags: prev[platform]?.hashtags ?? liveHashtags },
                                        }));
                                        setEditedPlatforms(prev => new Set([...prev, platform]));
                                      }}
                                      rows={5}
                                      className={`w-full rounded-xl px-3 py-2.5 text-sm border focus:outline-none focus:ring-2 resize-none transition-colors ${
                                        isOverLimit
                                          ? 'border-red-300 bg-red-50 focus:ring-red-200'
                                          : 'border-gray-200 bg-white focus:ring-violet-200 focus:border-violet-300'
                                      }`}
                                    />
                                  </div>

                                  {/* Hashtag chips */}
                                  <div>
                                    <label className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide block mb-1.5">Hashtags</label>
                                    <div className="flex flex-wrap gap-1.5 p-2.5 bg-white border border-gray-200 rounded-xl min-h-[38px]">
                                      {liveHashtags.map((tag: string, i: number) => (
                                        <span
                                          key={i}
                                          className="inline-flex items-center gap-1 text-xs text-violet-700 bg-violet-50 border border-violet-200 px-2 py-0.5 rounded-full"
                                        >
                                          {tag.startsWith('#') ? tag : `#${tag}`}
                                          <button
                                            onClick={() => {
                                              const next = liveHashtags.filter((_: string, idx: number) => idx !== i);
                                              setEditedContent(prev => ({
                                                ...prev,
                                                [platform]: { text: prev[platform]?.text ?? liveText, hashtags: next },
                                              }));
                                              setEditedPlatforms(prev => new Set([...prev, platform]));
                                            }}
                                            className="text-violet-400 hover:text-violet-700 transition-colors"
                                          >
                                            <X className="w-2.5 h-2.5" />
                                          </button>
                                        </span>
                                      ))}
                                      {/* Add tag input */}
                                      <input
                                        value={tagInput[platform] || ''}
                                        onChange={(e) => setTagInput(prev => ({ ...prev, [platform]: e.target.value }))}
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter' || e.key === ',') {
                                            e.preventDefault();
                                            const raw = (tagInput[platform] || '').trim().replace(/^#+/, '');
                                            if (!raw) return;
                                            const newTag = `#${raw}`;
                                            if (!liveHashtags.includes(newTag)) {
                                              setEditedContent(prev => ({
                                                ...prev,
                                                [platform]: { text: prev[platform]?.text ?? liveText, hashtags: [...liveHashtags, newTag] },
                                              }));
                                              setEditedPlatforms(prev => new Set([...prev, platform]));
                                            }
                                            setTagInput(prev => ({ ...prev, [platform]: '' }));
                                          }
                                        }}
                                        placeholder="Add tag, press Enter"
                                        className="text-xs text-gray-600 outline-none flex-1 min-w-[120px] placeholder:text-gray-300"
                                      />
                                    </div>
                                  </div>

                                  {/* Action row */}
                                  <div className="flex items-center gap-3 pt-1">
                                    <button
                                      onClick={() => {
                                        if (!result?.pipeline_id) return;
                                        const payload = { [platform]: { text: liveText, hashtags: liveHashtags } };
                                        patchContentMutation.mutate({ pipelineId: result.pipeline_id, content: payload });
                                        // Reflect saved state into result so subsequent create-posts gets it
                                        setResult(prev => prev ? {
                                          ...prev,
                                          platform_content: { ...prev.platform_content, [platform]: { text: liveText, hashtags: liveHashtags } },
                                        } : prev);
                                      }}
                                      disabled={patchContentMutation.isPending}
                                      className="flex items-center gap-1.5 px-4 py-1.5 bg-violet-600 text-white rounded-lg text-xs font-semibold hover:opacity-90 disabled:opacity-50 shadow-sm"
                                    >
                                      {patchContentMutation.isPending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                                      Save
                                    </button>
                                    <button
                                      onClick={() => {
                                        // Reset to AI original
                                        setEditedContent(prev => ({
                                          ...prev,
                                          [platform]: { text: content.text || '', hashtags: content.hashtags || [] },
                                        }));
                                        setEditedPlatforms(prev => {
                                          const next = new Set(prev);
                                          next.delete(platform);
                                          return next;
                                        });
                                      }}
                                      className="text-xs text-gray-400 hover:text-gray-600 transition-colors"
                                    >
                                      Reset to AI
                                    </button>
                                    <button
                                      onClick={() =>
                                        copyToClipboard(`${liveText}\n\n${liveHashtags.join(' ')}`)
                                      }
                                      className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600 ml-auto"
                                    >
                                      <Copy className="w-3 h-3" /> Copy
                                    </button>
                                    <button
                                      onClick={() =>
                                        navigate(`/compose?caption=${encodeURIComponent(liveText)}`)
                                      }
                                      className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600"
                                    >
                                      <Send className="w-3 h-3" /> Open in Composer
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Nav: go to Publish or Start Over */}
                  {result.status === "completed" && (
                    <div className="flex justify-between">
                      <button
                        onClick={resetWizard}
                        className="flex items-center gap-1.5 px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
                      >
                        <ArrowLeft className="w-4 h-4" /> Start Over
                      </button>
                      <button
                        onClick={handleGoToPublish}
                        className="flex items-center gap-1.5 px-5 py-2.5 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:opacity-90"
                      >
                        Next: Publish Settings <ArrowRight className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </>
              )}

              {/* Calendar Result */}
              {calResult && Array.isArray(calResult.calendar) && calResult.calendar.length === 0 && (
                <div className="bg-amber-50 rounded-xl border border-amber-200 p-4 text-center">
                  <AlertCircle className="w-8 h-8 text-amber-400 mx-auto mb-2" />
                  <p className="text-sm font-medium text-amber-800">No calendar entries generated</p>
                  <p className="text-xs text-amber-600 mt-1">The AI couldn&apos;t produce a content calendar for this topic. Try again with a broader topic or fewer days.</p>
                  <button onClick={resetWizard} className="mt-3 text-xs text-violet-600 hover:text-violet-800 font-medium">
                    <ArrowLeft className="w-3 h-3 inline mr-1" />Start Over
                  </button>
                </div>
              )}
              {calResult && Array.isArray(calResult.calendar) && calResult.calendar.length > 0 && (
                <>
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-gray-900">
                      {calResult.days as React.ReactNode}-Day Calendar · {calResult.total_posts as React.ReactNode} Posts
                    </h3>
                    {(calResult.posts_created as unknown[] | undefined)?.length ? (
                      <button
                        onClick={() => navigate("/posts")}
                        className="text-sm text-violet-600 hover:text-violet-800 flex items-center gap-1"
                      >
                        <Send className="w-3.5 h-3.5" /> View {(calResult.posts_created as unknown[]).length}{" "}
                        Drafts
                      </button>
                    ) : null}
                  </div>
                  {(calResult.calendar as unknown[]).map((day: unknown, di: number) => {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    const dayData = day as Record<string, any>;
                    return (
                    <div key={di} className="bg-white rounded-xl border border-gray-200 p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <div className="w-8 h-8 rounded-full bg-violet-100 flex items-center justify-center text-sm font-bold text-violet-700">
                          {dayData.day as React.ReactNode}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">Day {dayData.day as React.ReactNode}</p>
                          {dayData.theme ? <p className="text-xs text-gray-500">{dayData.theme as React.ReactNode}</p> : null}
                        </div>
                      </div>
                      <div className="space-y-2">
                        {((dayData.posts || []) as unknown[]).map((post: unknown, pi: number) => {
                          // eslint-disable-next-line @typescript-eslint/no-explicit-any
                          const postData = post as Record<string, any>;
                          const platInfo = PLATFORMS.find((p) => p.id === postData.platform);
                          return (
                            <div key={pi} className="bg-gray-50 rounded-lg p-3">
                              <div className="flex items-center gap-2 mb-1">
                                <div
                                  className={`w-2 h-2 rounded-full ${platInfo?.color || "bg-gray-400"}`}
                                />
                                <span className="text-xs font-medium text-gray-700">
                                  {platInfo?.label || (postData.platform as React.ReactNode)}
                                </span>
                                <span className="text-xs text-gray-400 ml-auto">
                                  {postData.best_time as React.ReactNode}
                                </span>
                              </div>
                              <p className="text-sm text-gray-800 line-clamp-3">{postData.caption as React.ReactNode}</p>
                              {(postData.hashtags as string[] | undefined)?.length && (
                                <p className="text-xs text-violet-600 mt-1">
                                  {((postData.hashtags as string[]) || []).join(" ")}
                                </p>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                    );
                  })}
                  <div className="flex justify-between">
                    <button
                      onClick={resetWizard}
                      className="flex items-center gap-1.5 px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
                    >
                      <ArrowLeft className="w-4 h-4" /> Start Over
                    </button>
                    {(calResult.posts_created as unknown[] | undefined)?.length ? (
                      <button
                        onClick={() => navigate("/posts")}
                        className="flex items-center gap-1.5 px-5 py-2.5 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:opacity-90"
                      >
                        <Send className="w-4 h-4" /> View {(calResult.posts_created as unknown[]).length} Draft
                        Posts
                      </button>
                    ) : null}
                  </div>
                </>
              )}
            </div>
          )}

          {/* ── Step 6: Publish ────────────────────────────── */}
          {wizardStep === "publish" && result && (
            <div className="max-w-none space-y-5">
              <h2 className="text-sm font-bold text-gray-900">Publish Settings</h2>

              {/* Publish Mode */}
              <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
                <label className="text-sm font-semibold text-gray-700">
                  How should we publish?
                </label>
                <div className="grid grid-cols-3 gap-3">
                  {(
                    [
                      {
                        id: "draft" as const,
                        label: "Save as Draft",
                        desc: "Review in Posts first",
                        icon: FileText,
                      },
                      {
                        id: "schedule" as const,
                        label: "Schedule",
                        desc: "Post at suggested times",
                        icon: Clock,
                      },
                      {
                        id: "publish_now" as const,
                        label: "Publish Now",
                        desc: "Post immediately",
                        icon: Send,
                      },
                    ] as const
                  ).map((mode) => (
                    <button
                      key={mode.id}
                      onClick={() => setPublishMode(mode.id)}
                      className={`p-3 rounded-lg border-2 text-left transition-all ${
                        publishMode === mode.id
                          ? "border-violet-500 bg-violet-50"
                          : "border-gray-200 hover:border-gray-400"
                      }`}
                    >
                      <mode.icon
                        className={`w-5 h-5 mb-1.5 ${publishMode === mode.id ? "text-violet-600" : "text-gray-400"}`}
                      />
                      <p className="text-sm font-medium text-gray-800">{mode.label}</p>
                      <p className="text-[10px] text-gray-500">{mode.desc}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Schedule Time Override */}
              {publishMode === "schedule" && Object.keys(result.suggested_times).length > 0 && (
                <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-3">
                  <label className="text-sm font-semibold text-gray-700">
                    Schedule Times (AI-suggested, adjust if needed)
                  </label>
                  <div className="space-y-2">
                    {Object.entries(result.suggested_times).map(([platform, isoTime]) => {
                      const platInfo = PLATFORMS.find((p) => p.id === platform);
                      const fallback = new Date(Date.now() + 3600000).toISOString().slice(0, 16);
                      const inputVal = customTimes[platform]
                        ? customTimes[platform].slice(0, 16)
                        : (isoTime || "").slice(0, 16) || fallback;
                      return (
                        <div key={platform} className="flex items-center gap-3">
                          <div
                            className={`w-2 h-2 rounded-full ${platInfo?.color || "bg-gray-400"}`}
                          />
                          <span className="text-sm text-gray-700 w-24">
                            {platInfo?.label || platform}
                          </span>
                          <input
                            type="datetime-local"
                            value={inputVal}
                            min={new Date().toISOString().slice(0, 16)}
                            onChange={(e) => {
                              const selected = new Date(e.target.value);
                              if (selected <= new Date()) {
                                toast.error("Schedule time must be in the future");
                                return;
                              }
                              const newTime = selected.toISOString();
                              setCustomTimes({ ...customTimes, [platform]: newTime });
                            }}
                            className="flex-1 border border-gray-300 rounded-lg px-3 py-1.5 text-sm"
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Summary */}
              <div className="bg-violet-50 rounded-xl border border-violet-200 p-4">
                <h4 className="text-sm font-semibold text-violet-800 mb-2">Summary</h4>
                <div className="grid grid-cols-2 gap-2 text-xs text-violet-700">
                  <div>Platforms: {selectedPlatforms.join(", ") || "—"}</div>
                  <div>Accounts: {selectedAccounts.length || "all matching"}</div>
                  <div>Quality: {result.quality_score}/10</div>
                  <div>Publish: {publishMode.replace(/_/g, " ")}</div>
                  <div>AI Cost: ${result.cost_usd.toFixed(4)}</div>
                  <div>
                    {(result.posts_created?.length || 0) > 0
                      ? `${result.posts_created?.length} post(s) already created`
                      : "Posts will be created"}
                  </div>
                </div>
              </div>

              {/* Action buttons */}
              <div className="flex justify-between">
                <button
                  onClick={() => setWizardStep("review")}
                  className="flex items-center gap-1.5 px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
                >
                  <ArrowLeft className="w-4 h-4" /> Back to Review
                </button>
                <button
                  onClick={handleFinalPublish}
                  disabled={createPostsMutation.isPending}
                  className="flex items-center gap-1.5 px-6 py-2.5 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:opacity-90 disabled:opacity-50"
                >
                  {createPostsMutation.isPending ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" /> Creating Posts...
                    </>
                  ) : (result.posts_created?.length || 0) > 0 ? (
                    <>
                      <Eye className="w-4 h-4" /> View Posts
                    </>
                  ) : publishMode === "publish_now" ? (
                    <>
                      <Send className="w-4 h-4" /> Publish Now
                    </>
                  ) : publishMode === "schedule" ? (
                    <>
                      <Clock className="w-4 h-4" /> Schedule Posts
                    </>
                  ) : (
                    <>
                      <FileText className="w-4 h-4" /> Create Drafts
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
          </div>
        </div>
      )}

      {/* ═══════════ HISTORY VIEW ═══════════ */}
      {activeView === "history" && (
        <div className="flex-1 overflow-y-auto px-4 py-6">
        <div className="max-w-3xl mx-auto space-y-3">
          {history.length === 0 && (
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl border border-gray-100 p-12 text-center shadow-sm">
              <div className="relative mx-auto mb-4 w-12 h-12">
                <div className="absolute inset-0 rounded-2xl bg-gray-200/30 blur-lg scale-150" />
                <div className="relative w-12 h-12 rounded-2xl bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
                  <Clock className="w-6 h-6 text-gray-300" />
                </div>
              </div>
              <h3 className="text-sm font-medium text-gray-500">No pipeline runs yet</h3>
              <p className="text-sm text-gray-400 mt-1">
                Your AI orchestrator history will appear here
              </p>
            </div>
          )}

          {history.map((item) => (
            <div key={item.id} className="bg-white/80 backdrop-blur-sm rounded-2xl border border-gray-100 overflow-hidden shadow-sm shadow-gray-100/80 hover:shadow-md hover:border-gray-200 transition-all duration-200">
              <button
                onClick={async () => {
                  const newId = expandedHistoryItem === item.id ? null : item.id;
                  setExpandedHistoryItem(newId);
                  if (newId && !runDetailCache[newId]) {
                    try {
                      const resp = await orchestratorAPI.getRunDetail(newId);
                      setRunDetailCache((prev) => ({ ...prev, [newId]: resp.data as PipelineRunDetail }));
                    } catch {
                      // Mark as failed so UI doesn't show infinite spinner
                      setRunDetailCache((prev) => ({ ...prev, [newId]: null as unknown as PipelineRunDetail }));
                    }
                  }
                }}
                className="w-full flex items-center gap-3 px-5 py-4 hover:bg-gray-50/50 transition-colors duration-200"
              >
                <div className="flex-1 text-left">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-medium text-sm text-gray-900">
                      {item.topic || item.pipeline_name}
                    </span>
                    {statusBadge(item.status)}
                  </div>
                  <div className="flex items-center gap-3 text-xs text-gray-500">
                    <span>{item.pipeline_name.replace(/_/g, " ")}</span>
                    <span>·</span>
                    <span>{item.steps_completed.length} steps</span>
                    {item.quality_score > 0 && (
                      <>
                        <span>·</span>
                        <span className="flex items-center gap-0.5">
                          <Star className="w-3 h-3 text-yellow-500" /> {item.quality_score}
                        </span>
                      </>
                    )}
                    {item.cost_usd > 0 && (
                      <>
                        <span>·</span>
                        <span className="flex items-center gap-0.5">
                          <DollarSign className="w-3 h-3" /> {item.cost_usd.toFixed(4)}
                        </span>
                      </>
                    )}
                    <span>·</span>
                    <span>{new Date(item.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
                {expandedHistoryItem === item.id ? (
                  <ChevronUp className="w-4 h-4 text-gray-400" />
                ) : (
                  <ChevronDown className="w-4 h-4 text-gray-400" />
                )}
              </button>

              {expandedHistoryItem === item.id && (() => {
                const detail = runDetailCache[item.id] as PipelineRunDetail | undefined;
                return (
                <div className="px-5 pb-5 border-t border-gray-100 pt-4 space-y-4">
                  {/* Steps completed */}
                  <div className="flex items-center gap-1 flex-wrap">
                    {item.steps_completed.map((step) => (
                      <span key={step} className="px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs">
                        {STEP_LABELS[step]?.label || step}
                      </span>
                    ))}
                  </div>

                  {!detail && (
                    <p className="text-xs text-gray-400 italic">Loading details...</p>
                  )}

                  {detail && (
                    <>
                      {/* Strategy / Content Angle */}
                      {detail.content_angle && (
                        <div>
                          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Content Angle</h4>
                          <p className="text-sm text-gray-800 bg-gray-50 rounded-lg p-3">{detail.content_angle}</p>
                        </div>
                      )}

                      {/* Key Points */}
                      {detail.key_points && detail.key_points.length > 0 && (
                        <div>
                          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Key Points</h4>
                          <div className="flex flex-wrap gap-1.5">
                            {detail.key_points.map((kp, i) => (
                              <span key={i} className="text-xs bg-violet-50 text-violet-700 px-2 py-0.5 rounded">{kp}</span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Base Caption */}
                      {detail.base_caption && (
                        <div>
                          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Generated Caption</h4>
                          <p className="text-sm text-gray-800 bg-gray-50 rounded-lg p-3 whitespace-pre-wrap leading-relaxed">{detail.base_caption}</p>
                        </div>
                      )}

                      {/* Hashtags */}
                      {detail.hashtags && detail.hashtags.length > 0 && (
                        <div>
                          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Hashtags</h4>
                          <p className="text-sm text-blue-600">{detail.hashtags.join(" ")}</p>
                        </div>
                      )}

                      {/* Platform-specific content */}
                      {detail.platform_content && Object.keys(detail.platform_content).length > 0 && (
                        <div>
                          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Platform Content</h4>
                          <div className="space-y-2">
                            {Object.entries(detail.platform_content).map(([platform, content]: [string, unknown]) => (
                              <div key={platform} className="bg-gray-50 rounded-lg p-3">
                                <span className="text-xs font-bold text-gray-600 uppercase">{platform}</span>
                                <p className="text-sm text-gray-800 mt-1 whitespace-pre-wrap">{(content as Record<string, unknown>)?.caption as string || (content as Record<string, unknown>)?.text as string || JSON.stringify(content)}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Review Notes */}
                      {detail.review_notes && detail.review_notes.length > 0 && (
                        <div>
                          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Review Notes</h4>
                          <div className="space-y-1">
                            {detail.review_notes.map((note, i) => (
                              <p key={i} className="text-xs text-gray-600 bg-amber-50 rounded px-2.5 py-1.5">
                                {note}
                              </p>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Settings row */}
                      <div className="flex flex-wrap gap-3 text-xs text-gray-500 pt-1">
                        {detail.target_platforms.length > 0 && (
                          <span>Platforms: {detail.target_platforms.join(", ")}</span>
                        )}
                        {detail.tone && <span>Tone: {detail.tone}</span>}
                        {detail.publish_mode && <span>Publish: {detail.publish_mode.replace(/_/g, " ")}</span>}
                      </div>

                      {/* Errors */}
                      {detail.errors && detail.errors.length > 0 && (
                        <div>
                          <h4 className="text-xs font-semibold text-red-500 uppercase tracking-wider mb-1">Errors</h4>
                          {detail.errors.map((err, i) => (
                            <p key={i} className="text-xs text-red-600 bg-red-50 rounded px-2.5 py-1.5">{err}</p>
                          ))}
                        </div>
                      )}
                    </>
                  )}

                  {/* Actions */}
                  <div className="flex gap-2 pt-1">
                    {(item.status === "paused" || item.status === "failed") && (
                      <button
                        onClick={() => resumeMutation.mutate({ pipelineId: item.id, stepsCompleted: item.steps_completed, pipelineName: item.pipeline_name })}
                        className="text-xs text-violet-600 hover:underline flex items-center gap-1"
                      >
                        <Play className="w-3 h-3" /> {item.status === "failed" ? "Retry" : "Resume"}
                      </button>
                    )}
                    {item.post_ids.length > 0 && (
                      <button
                        onClick={() => navigate("/posts")}
                        className="text-xs text-gray-600 hover:underline flex items-center gap-1"
                      >
                        <FileText className="w-3 h-3" /> View Posts
                      </button>
                    )}
                  </div>
                </div>
                );
              })()}
            </div>
          ))}
        </div>
        </div>
      )}

      {/* ═══════════ PROMPTS VIEW ═══════════ */}
      {activeView === "prompts" && (
        <div className="flex-1 overflow-y-auto px-4 py-6">
          <div className="max-w-3xl mx-auto">
            <PromptsEditor />
          </div>
        </div>
      )}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════
//  Prompts Editor Sub-Component
// ═══════════════════════════════════════════════════════════════════

const AGENT_LABELS: Record<string, string> = {
  strategy_focus: "Strategy Agent",
  content_style: "Content Agent",
  optimization_focus: "Optimization Agent",
  review_criteria: "Review Agent",
};

const PERSONA_FIELDS: { key: string; label: string }[] = [
  { key: "strategy_persona", label: "Strategy Agent Persona" },
  { key: "content_persona", label: "Content Agent Persona" },
  { key: "review_persona", label: "Review Agent Persona" },
];

const FLOW_LABELS: Record<string, string> = {
  quick_post: "Quick Post",
  full_control: "Full Control",
  repurpose: "Optimize & Repurpose",
  viral_rewrite: "Viral Rewriter",
  calendar: "Content Calendar",
  _default: "Default (all flows)",
};

function PromptsEditor() {
  const { t } = useTranslation();
  const [defaults, setDefaults] = useState<Record<string, Record<string, string>> | null>(null);
  const [overrides, setOverrides] = useState<Record<string, unknown>>({});
  const [editAgent, setEditAgent] = useState<string | null>(null);
  const [editFlow, setEditFlow] = useState<string | null>(null);
  const [editText, setEditText] = useState("");
  const [editPersonaKey, setEditPersonaKey] = useState<string | null>(null);
  const [editPersonaText, setEditPersonaText] = useState("");
  const [dirty, setDirty] = useState(false);

  // Load defaults + overrides
  const { data: defaultsData, isLoading: loadingDefaults } = useQuery({
    queryKey: ["prompt-defaults"],
    queryFn: () => orchestratorAPI.getPromptDefaults(),
  });

  const { data: overridesData, isLoading: loadingOverrides, refetch: refetchOverrides } = useQuery({
    queryKey: ["prompt-overrides"],
    queryFn: () => orchestratorAPI.getPromptOverrides(),
  });

  useEffect(() => {
    if (defaultsData) {
      const d = ((defaultsData as unknown) as Record<string, unknown>)?.data as Record<string, unknown> | undefined;
      setDefaults((d?.defaults as Record<string, Record<string, string>>) || {});
    }
  }, [defaultsData]);

  useEffect(() => {
    if (overridesData) setOverrides(((((overridesData as unknown) as Record<string, unknown>)?.data as Record<string, unknown>) || {}));
  }, [overridesData]);

  const saveMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => orchestratorAPI.savePromptOverrides(data),
    onSuccess: () => {
      toast.success(t('orch_text_save_success'));
      setDirty(false);
      refetchOverrides();
    },
    onError: (e: unknown) => toast.error(getErrorMessage(e, t('orch_text_save_error'))),
  });

  const resetMutation = useMutation({
    mutationFn: () => orchestratorAPI.resetPromptOverrides(),
    onSuccess: () => {
      toast.success(t('orch_text_reset_success'));
      setOverrides({});
      setDirty(false);
      refetchOverrides();
    },
    onError: (e: unknown) => toast.error(getErrorMessage(e, t('orch_text_reset_error'))),
  });

  const isLoading = loadingDefaults || loadingOverrides;

  const openEditor = (agentKey: string, flowId: string) => {
    const userVal = (overrides[agentKey] as Record<string, string> | undefined)?.[flowId];
    const defaultVal = defaults?.[agentKey]?.[flowId] || "";
    setEditAgent(agentKey);
    setEditFlow(flowId);
    setEditText(userVal || defaultVal);
  };

  const saveEdit = () => {
    if (!editAgent || !editFlow) return;
    setOverrides((prev: Record<string, unknown>) => ({
      ...prev,
      [editAgent]: { ...((prev[editAgent] as Record<string, unknown>) || {}), [editFlow]: editText },
    }));
    setDirty(true);
    setEditAgent(null);
    setEditFlow(null);
  };

  const clearOverride = (agentKey: string, flowId: string) => {
    setOverrides((prev: Record<string, unknown>) => {
      const agentOverrides = { ...((prev[agentKey] as Record<string, unknown>) || {}) };
      delete agentOverrides[flowId];
      return { ...prev, [agentKey]: agentOverrides };
    });
    setDirty(true);
  };

  const openPersonaEditor = (key: string) => {
    setEditPersonaKey(key);
    setEditPersonaText((overrides[key] as string) || "");
  };

  const savePersona = () => {
    if (!editPersonaKey) return;
    setOverrides((prev: Record<string, unknown>) => ({ ...prev, [editPersonaKey!]: editPersonaText }));
    setDirty(true);
    setEditPersonaKey(null);
  };

  const handleSaveAll = () => {
    saveMutation.mutate(overrides);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-violet-500" />
      </div>
    );
  }

  const agentKeys = Object.keys(AGENT_LABELS);
  const flowIds = defaults ? Object.keys(defaults[agentKeys[0]] || {}) : [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-sm font-bold text-gray-900">Prompt Templates</h2>
          <p className="text-sm text-gray-500">
            Customize what each AI agent does per flow. Overrides are highlighted in violet.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => resetMutation.mutate()}
            disabled={resetMutation.isPending}
            className="px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 rounded-lg border border-red-200"
          >
            <RotateCcw className="w-3.5 h-3.5 inline mr-1" />
            Reset All
          </button>
          <button
            onClick={handleSaveAll}
            disabled={!dirty || saveMutation.isPending}
            className="px-4 py-1.5 text-sm bg-violet-600 text-white rounded-lg hover:bg-violet-700 disabled:opacity-40"
          >
            {saveMutation.isPending ? (
              <Loader2 className="w-3.5 h-3.5 inline animate-spin mr-1" />
            ) : (
              <Check className="w-3.5 h-3.5 inline mr-1" />
            )}
            Save Changes
          </button>
        </div>
      </div>

      {/* Persona Overrides */}
      <div className="bg-white/80 backdrop-blur-sm rounded-2xl border border-gray-100 p-5 space-y-3 shadow-sm shadow-gray-100/80">
        <h3 className="text-sm font-semibold text-gray-700">Agent Personas</h3>
        <p className="text-xs text-gray-400">The system role/identity for each agent. Leave empty for defaults.</p>
        <div className="space-y-2">
          {PERSONA_FIELDS.map(({ key, label }) => (
            <div key={key} className="flex items-center gap-3">
              <span className="text-sm text-gray-700 w-48">{label}</span>
              {overrides[key] ? (
                <div className="flex-1 flex items-center gap-2">
                  <span className="text-xs text-violet-600 truncate flex-1 bg-violet-50 px-2 py-1 rounded">
                    {(overrides[key] as string).slice(0, 80)}…
                  </span>
                  <button onClick={() => openPersonaEditor(key)} className="text-xs text-violet-600 hover:underline">
                    Edit
                  </button>
                  <button
                    onClick={() => {
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      setOverrides((prev: Record<string, any>) => ({ ...prev, [key]: "" }));
                      setDirty(true);
                    }}
                    className="text-xs text-red-500 hover:underline"
                  >
                    Clear
                  </button>
                </div>
              ) : (
                <button onClick={() => openPersonaEditor(key)} className="text-xs text-gray-500 hover:text-violet-600">
                  + Customize
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Per-Agent Per-Flow Grid */}
      {agentKeys.map((agentKey) => (
        <div key={agentKey} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-5 py-3 border-b border-gray-100 bg-gray-50">
            <h3 className="font-semibold text-sm text-gray-800">{AGENT_LABELS[agentKey]}</h3>
          </div>
          <div className="divide-y divide-gray-100">
            {flowIds.map((flowId) => {
              const agentOverrides = overrides[agentKey] as Record<string, string> | undefined;
              const hasOverride = !!agentOverrides?.[flowId];
              const displayText = agentOverrides?.[flowId] || defaults?.[agentKey]?.[flowId] || "";
              return (
                <div
                  key={flowId}
                  className={`px-5 py-3 flex items-start gap-3 ${hasOverride ? "bg-violet-50/50" : ""}`}
                >
                  <div className="w-36 shrink-0">
                    <span className="text-sm font-medium text-gray-700">
                      {FLOW_LABELS[flowId] || flowId}
                    </span>
                    {hasOverride && (
                      <span className="ml-1.5 text-[9px] bg-violet-200 text-violet-700 px-1.5 py-0.5 rounded-full font-medium">
                        custom
                      </span>
                    )}
                  </div>
                  <p className="flex-1 text-xs text-gray-500 line-clamp-3 whitespace-pre-wrap">
                    {displayText.slice(0, 200)}{displayText.length > 200 ? "…" : ""}
                  </p>
                  <div className="flex gap-2 shrink-0">
                    <button
                      onClick={() => openEditor(agentKey, flowId)}
                      className="text-xs text-violet-600 hover:underline"
                    >
                      {hasOverride ? "Edit" : "Customize"}
                    </button>
                    {hasOverride && (
                      <button
                        onClick={() => clearOverride(agentKey, flowId)}
                        className="text-xs text-red-500 hover:underline"
                      >
                        Reset
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {/* ── Edit Modal (Focus Block) ── */}
      {editAgent && editFlow && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl mx-4 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-gray-900">
                  {AGENT_LABELS[editAgent]} — {FLOW_LABELS[editFlow] || editFlow}
                </h3>
                <p className="text-xs text-gray-400 mt-0.5">
                  This text replaces the flow-specific focus/style block in the prompt.
                </p>
              </div>
              <button onClick={() => setEditAgent(null)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6">
              <textarea
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
                rows={12}
                className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm font-mono leading-relaxed focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                placeholder="Enter your custom prompt focus/style block..."
              />
              <div className="flex justify-between items-center mt-4">
                <button
                  onClick={() => {
                    setEditText(defaults?.[editAgent]?.[editFlow] || "");
                  }}
                  className="text-xs text-gray-500 hover:text-gray-700"
                >
                  <RotateCcw className="w-3 h-3 inline mr-1" />
                  Restore Default
                </button>
                <div className="flex gap-2">
                  <button
                    onClick={() => setEditAgent(null)}
                    className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={saveEdit}
                    className="px-4 py-2 text-sm bg-violet-600 text-white rounded-lg hover:bg-violet-700"
                  >
                    Apply
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Edit Modal (Persona) ── */}
      {editPersonaKey && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-xl mx-4 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="font-semibold text-gray-900">
                {PERSONA_FIELDS.find((p) => p.key === editPersonaKey)?.label}
              </h3>
              <button onClick={() => setEditPersonaKey(null)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6">
              <textarea
                value={editPersonaText}
                onChange={(e) => setEditPersonaText(e.target.value)}
                rows={4}
                className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm focus:ring-2 focus:ring-violet-500"
                placeholder='e.g. "You are a Gen-Z social media expert who writes viral content for tech startups."'
              />
              <div className="flex justify-end gap-2 mt-4">
                <button
                  onClick={() => setEditPersonaKey(null)}
                  className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  onClick={savePersona}
                  className="px-4 py-2 text-sm bg-violet-600 text-white rounded-lg hover:bg-violet-700"
                >
                  Apply
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
